import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  Box,
  Text,
  Heading,
  Button,
  Flex,
  Spinner,
  Badge,
  Tag,
  TagLabel,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Progress,
  HStack,
  VStack,
  Divider,
  Icon,
  useToast,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody, ModalCloseButton,
  Image,
  useDisclosure,
  Select
} from "@chakra-ui/react";
import { CheckIcon, CloseIcon, ChevronRightIcon, ChevronLeftIcon, ChevronUpIcon, ChevronDownIcon, RepeatIcon, ViewIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { jwtDecode } from "jwt-decode"; // Updated import
import { CameraSearchSelect, UserSearchSelect, AlgorithmSearchSelect } from "./common/SearchSelects";
import { getInfractionClassLabel, INFRACTION_CLASS_OPTIONS } from "../utils/formatters";
import { filterAllowedAlgorithms } from "../utils/algorithms";
import { toStorageImageApiSegment } from "../utils/storageImagePath";
import PlateWithSuggestion from "./common/PlateWithSuggestion";
import { fetchPlateSuggestions } from "../utils/plateSuggestions";
import { getSessionPermissions } from "../utils/auth";

// Componente de ícones customizados
const CustomIcon = ({ children }) => (
  <Icon viewBox="0 0 24 24" mr={2}>
    {children}
  </Icon>
);

// Ícones
const BatchIcon = (props) => (
  <CustomIcon {...props}>
    <path fill="currentColor" d="M7,5H21V7H7V5M7,13V11H21V13H7M7,19V17H21V19H7M3,5A1,1 0 0,1 4,4A1,1 0 0,1 5,5A1,1 0 0,1 4,6A1,1 0 0,1 3,5M3,13A1,1 0 0,1 4,12A1,1 0 0,1 5,13A1,1 0 0,1 4,14A1,1 0 0,1 3,13M3,21A1,1 0 0,1 4,20A1,1 0 0,1 5,21A1,1 0 0,1 4,22A1,1 0 0,1 3,21Z" />
  </CustomIcon>
);

const AssignIcon = (props) => (
  <CustomIcon {...props}>
    <path fill="currentColor" d="M12,5.5A3.5,3.5 0 0,1 15.5,9A3.5,3.5 0 0,1 12,12.5A3.5,3.5 0 0,1 8.5,9A3.5,3.5 0 0,1 12,5.5M5,8C5.56,8 6.08,8.15 6.53,8.42C6.38,9.85 6.8,11.27 7.66,12.38C7.16,13.34 6.16,14 5,14A3,3 0 0,1 2,11A3,3 0 0,1 5,8M19,8A3,3 0 0,1 22,11A3,3 0 0,1 19,14C17.84,14 16.84,13.34 16.34,12.38C17.2,11.27 17.62,9.85 17.47,8.42C17.92,8.15 18.44,8 19,8M5.5,18.25C5.5,16.18 8.41,14.5 12,14.5C15.59,14.5 18.5,16.18 18.5,18.25V20H5.5V18.25M0,20V18.5C0,17.11 1.89,15.94 4.45,15.6C3.86,16.28 3.5,17.22 3.5,18.25V20H0M24,20H20.5V18.25C20.5,17.22 20.14,16.28 19.55,15.6C22.11,15.94 24,17.11 24,18.5V20Z" />
  </CustomIcon>
);

const RefreshIcon = (props) => (
  <CustomIcon {...props}>
    <path fill="currentColor" d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z" />
  </CustomIcon>
);

const ReviewIcon = (props) => (
  <CustomIcon {...props}>
    <path fill="currentColor" d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z" />
  </CustomIcon>
);

// Não utilizado atualmente, mas mantido para uso futuro
// const CompleteIcon = (props) => (
//   <CustomIcon {...props}>
//     <path fill="currentColor" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
//   </CustomIcon>
// );

const CameraIcon = (props) => (
  <CustomIcon {...props}>
    <path fill="currentColor" d="M4,4H7L9,2H15L17,4H20A2,2 0 0,1 22,6V18A2,2 0 0,1 20,20H4A2,2 0 0,1 2,18V6A2,2 0 0,1 4,4M12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7M12,9A3,3 0 0,1 15,12A3,3 0 0,1 12,15A3,3 0 0,1 9,12A3,3 0 0,1 12,9Z" />
  </CustomIcon>
);

const AlgorithmIcon = (props) => (
  <CustomIcon {...props}>
    <path fill="currentColor" d="M7,5H21V7H7V5M7,13V11H21V13H7M4,4.5A1.5,1.5 0 0,1 5.5,6A1.5,1.5 0 0,1 4,7.5A1.5,1.5 0 0,1 2.5,6A1.5,1.5 0 0,1 4,4.5M4,10.5A1.5,1.5 0 0,1 5.5,12A1.5,1.5 0 0,1 4,13.5A1.5,1.5 0 0,1 2.5,12A1.5,1.5 0 0,1 4,10.5M7,19V17H21V19H7M4,16.5A1.5,1.5 0 0,1 5.5,18A1.5,1.5 0 0,1 4,19.5A1.5,1.5 0 0,1 2.5,18A1.5,1.5 0 0,1 4,16.5Z" />
  </CustomIcon>
);

const CalendarIcon = (props) => (
  <CustomIcon {...props}>
    <path fill="currentColor" d="M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z" />
  </CustomIcon>
);

// Adicionar ícones para filtros
const FilterIcon = (props) => (
  <CustomIcon {...props}>
    <path fill="currentColor" d="M14,12V19.88C14.04,20.18 13.94,20.5 13.71,20.71C13.32,21.1 12.69,21.1 12.3,20.71L10.29,18.7C10.06,18.47 9.96,18.16 10,17.87V12H9.97L4.21,4.62C3.87,4.19 3.95,3.56 4.38,3.22C4.57,3.08 4.78,3 5,3V3H19V3C19.22,3 19.43,3.08 19.62,3.22C20.05,3.56 20.13,4.19 19.79,4.62L14.03,12H14Z" />
  </CustomIcon>
);

// NOTE: removed unused `formatDate` helper from this file to satisfy ESLint.
// Use shared formatter from `src/utils/formatters` if needed.

// Nova função para formatar data com dia da semana destacado
const formatDateWithWeekday = (dateString) => {
  if (!dateString) return { text: 'Não disponível', isWeekend: false };

  try {
    const date = new Date(dateString);
    const dayNames = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
    const dayOfWeek = date.getDay();
    const dayName = dayNames[dayOfWeek];
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Domingo (0) ou Sábado (6)

    const formattedDate = date.toLocaleDateString('pt-BR') + ' - ' + date.toLocaleTimeString('pt-BR');

    return {
      text: `${dayName}, ${formattedDate}`,
      isWeekend
    };
  } catch (error) {
    return { text: dateString, isWeekend: false };
  }
};

// Mapas de status
const getBatchStatusColor = (status) => {
  const colorMap = {
    'open': 'blue',
    'in_progress': 'yellow',
    'completed': 'green'
  };

  return colorMap[status] || 'gray';
};

const getBatchStatusText = (status) => {
  const statusMap = {
    'open': 'Disponível',
    'in_progress': 'Em Análise',
    'completed': 'Concluído'
  };

  return statusMap[status] || status;
};

const getInfractionStatusColor = (status) => {
  const colorMap = {
    'pending': 'yellow',
    'confirmed': 'green',
    'rejected': 'red'
  };

  return colorMap[status] || 'gray';
};

const getInfractionStatusText = (status) => {
  const statusMap = {
    'pending': 'Pendente',
    'confirmed': 'Confirmada',
    'rejected': 'Rejeitada'
  };

  return statusMap[status] || status;
};

// Card de Lote
const BatchCard = ({ batch, onAssign, onReview, onUnassign, isUserBatch, loadingReviewBatchId, loadingUnassignBatchId, userLevel, hasBatchInProgress, canAssignMoreBatches, assignRandomBatch }) => {
  const createdDate = formatDateWithWeekday(batch.created_at);
  const completedDate = batch.completed_at ? formatDateWithWeekday(batch.completed_at) : null;

  return (
    <Card variant="outline" size="sm" borderRadius="lg" boxShadow="md"
      _hover={{ transform: "translateY(-5px)", boxShadow: "lg" }}
      transition="transform 0.2s, box-shadow 0.2s">
      <CardHeader pb={2}>
        <Flex justify="space-between" align="center">
          <Badge colorScheme={getBatchStatusColor(batch.status)} px={2} py={1} borderRadius="md">
            {getBatchStatusText(batch.status)}
          </Badge>
          <Text fontSize="sm" color="gray.500">
            ID: {batch.id}
          </Text>
        </Flex>
        <Heading size="sm" mt={2}>{batch.name}</Heading>
      </CardHeader>
      <CardBody py={2}>
        <VStack align="stretch" spacing={2}>
          <HStack>
            <CameraIcon color="green.500" />
            <Text fontSize="sm">
              {batch.Camera?.name || 'Câmera não especificada'}
            </Text>
          </HStack>

          <HStack>
            <Box>
              <Text fontSize="xs" color="gray.500">Criado em:</Text>
              <Text
                fontSize="sm"
                color={createdDate.isWeekend ? "red.500" : "inherit"}
                fontWeight={createdDate.isWeekend ? "bold" : "normal"}
              >
                {createdDate.text}
              </Text>
            </Box>
          </HStack>
          {batch.assignee && (
            <HStack>
              <AssignIcon color="orange.500" />
              <Text fontSize="sm">
                Atribuído a: {batch.assignee?.username || 'Usuário desconhecido'}
              </Text>
            </HStack>
          )}
          {batch.completed_at && completedDate && (
            <HStack>
              <CalendarIcon color="orange.500" />
              <Box>
                <Text fontSize="xs" color="gray.500">Finalizado em:</Text>
                <Text
                  fontSize="sm"
                  color={completedDate.isWeekend ? "red.500" : "inherit"}
                  fontWeight={completedDate.isWeekend ? "bold" : "normal"}
                >
                  {completedDate.text}
                </Text>
              </Box>
            </HStack>
          )}
          <Box mt={2}>
            <Text fontSize="xs" mb={1}>Progresso de análise: {batch.analyzed_infractions} de {batch.total_infractions}</Text>
            <Progress
              value={(batch.analyzed_infractions / batch.total_infractions) * 100}
              colorScheme={
                batch.status === 'completed' ? 'green' :
                  batch.status === 'in_progress' ? 'yellow' : 'blue'
              }
              size="sm"
              borderRadius="md"
            />
          </Box>
        </VStack>
      </CardBody>
      <CardFooter pt={2}>
        <VStack width="full" spacing={2}>
          {batch.status === 'open' && !batch.assignee && userLevel > 3 && (
            <Button
              colorScheme="blue"
              size="sm"
              width="full"
              leftIcon={<AssignIcon />}
              onClick={() => onAssign(batch.id)}
              isDisabled={!canAssignMoreBatches || loadingReviewBatchId === batch.id}
              isLoading={loadingReviewBatchId === batch.id}
              loadingText="Atribuindo"
              title={!canAssignMoreBatches ? "Você já possui 3 lotes em análise" : "Atribuir este lote a mim"}
            >
              Atribuir a Mim
            </Button>
          )}
          {batch.status === 'in_progress' && isUserBatch && (
            <Button
              colorScheme="yellow"
              size="sm"
              width="full"
              leftIcon={<ReviewIcon />}
              onClick={() => onReview(batch.id)}
              isLoading={loadingReviewBatchId === batch.id}
              loadingText="Carregando"
            >
              Continuar Análise
            </Button>
          )}
          {batch.status === 'in_progress' && !isUserBatch && batch.assignee && userLevel >= 4 && (
            <Button
              colorScheme="blue"
              size="sm"
              width="full"
              leftIcon={<ReviewIcon />}
              onClick={() => onReview(batch.id)}
              isLoading={loadingReviewBatchId === batch.id}
              loadingText="Carregando"
            >
              Ver Detalhes
            </Button>
          )}
          {batch.status === 'in_progress' && (batch.assignee || batch.assignee_id) && userLevel >= 4 && onUnassign && (
            <Button
              colorScheme="red"
              size="sm"
              width="full"
              variant="outline"
              onClick={() => onUnassign(batch.id)}
              isLoading={loadingUnassignBatchId === batch.id}
              loadingText="Desvinculando"
            >
              Desvincular Lote
            </Button>
          )}
          {batch.status === 'completed' && (
            <Button
              colorScheme="green"
              size="sm"
              width="full"
              leftIcon={<ReviewIcon />}
              onClick={() => onReview(batch.id)}
              variant="outline"
              isLoading={loadingReviewBatchId === batch.id}
              loadingText="Carregando"
            >
              Ver Detalhes
            </Button>
          )}
        </VStack>
      </CardFooter>
    </Card>
  );
};

// Componente principal
const Batches = () => {
  const [statistics, setStatistics] = useState({
    batches: { total: 0, open: 0, inProgress: 0, completed: 0 },
    infractions: { pending: 0, confirmed: 0, rejected: 0, inBatch: 0, notInBatch: 0 }
  });

  const [batches, setBatches] = useState({
    all: { items: [], loading: true, page: 1, hasMore: true },
    open: { items: [], loading: true, page: 1, hasMore: true },
    inProgress: { items: [], loading: true, page: 1, hasMore: true },
    completed: { items: [], loading: true, page: 1, hasMore: true },
    user: { items: [], loading: true, page: 1, hasMore: true }
  });

  const [sortOption, setSortOption] = useState({
    field: 'created_at',
    direction: 'ASC',  // Alterado para ASC (mais antigos primeiro)
    initialized: false
  });
  // Adicionar estados para filtros
  const [filters, setFilters] = useState({
    batchId: '',
    assigneeId: '',
    startDate: '',
    endDate: '',
    cameraId: '',
    algorithmId: '',
    infractionClass: ''
  });

  // Estado para armazenar filtros temporários (antes de aplicar)
  const [pendingFilters, setPendingFilters] = useState({
    batchId: '',
    assigneeId: '',
    startDate: '',
    endDate: '',
    cameraId: '',
    algorithmId: '',
    infractionClass: ''
  });

  // Estado para armazenar a lista de usuários para o filtro
  const [users, setUsers] = useState([]);

  // Estado para verificar se usuários estão sendo carregados
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Estado para controlar a visibilidade do painel de filtros
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [canScrollTabsLeft, setCanScrollTabsLeft] = useState(false);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);
  const [isDraggingTabs, setIsDraggingTabs] = useState(false);
  const [tabsDragStartX, setTabsDragStartX] = useState(0);
  const [tabsScrollStartLeft, setTabsScrollStartLeft] = useState(0);
  const tabsCarouselRef = useRef(null);

  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedInfraction, setSelectedInfraction] = useState(null);
  const [infractions, setInfractions] = useState([]); 
  const [currentInfractionIndex, setCurrentInfractionIndex] = useState(0);
  const [loadingInfractions, setLoadingInfractions] = useState(false);
  const [creatingBatches, setCreatingBatches] = useState(false);
  // Estado para controlar se um lote está sendo atribuído (não usado atualmente)
  // const [assigningBatch, setAssigningBatch] = useState(false);
  const [submittingBatchReviews, setSubmittingBatchReviews] = useState(false);
  const [userLevel, setUserLevel] = useState(null);
  const [userId, setUserId] = useState(null); // Adicionar userId
  const [canEditBatch, setCanEditBatch] = useState(true); // Controlar se pode editar o lote
  const [loadingReviewBatchId, setLoadingReviewBatchId] = useState(null);
  const [loadingUnassignBatchId, setLoadingUnassignBatchId] = useState(null);
  const [isLoadingBatchModal, setIsLoadingBatchModal] = useState(false);
  const [batchLoadingMessage, setBatchLoadingMessage] = useState('Carregando lote...');
  const [backgroundImagesLoading, setBackgroundImagesLoading] = useState(false);
  const [backgroundImagesLoaded, setBackgroundImagesLoaded] = useState(0);
  const [backgroundImagesTotal, setBackgroundImagesTotal] = useState(0);
  const [hasUnsavedReviews, setHasUnsavedReviews] = useState(false);
  const hasBatchInProgress = batches.user.items.some(b => b.status === 'in_progress');
  const userActiveBatchesCount = batches.user.items.filter(b => b.status === 'in_progress').length;
  const MAX_BATCHES_PER_USER = 3;
  const canAssignMoreBatches = userActiveBatchesCount < MAX_BATCHES_PER_USER;
  // Adicionar estados para câmeras e algoritmos
  const [cameras, setCameras] = useState([]);
  const [algorithms, setAlgorithms] = useState([]);
  const [loadingCameras, setLoadingCameras] = useState(false);
  const [loadingAlgorithms, setLoadingAlgorithms] = useState(false);

  // Estados para dados analíticos
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [analyticsLoadedAt, setAnalyticsLoadedAt] = useState(null);

  // Estados para estatísticas por período
  const [periodStats, setPeriodStats] = useState(null);
  const [loadingPeriodStats, setLoadingPeriodStats] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [customPeriodStart, setCustomPeriodStart] = useState('');
  const [customPeriodEnd, setCustomPeriodEnd] = useState('');

  // Estados para filtros analíticos
  const [analyticsFilters, setAnalyticsFilters] = useState({
    sortBy: 'name', // 'name' ou 'count'
    sortOrder: 'ASC', // 'ASC' ou 'DESC'
    showOnlyAboveThreshold: false,
    thresholdValue: 0,
    batchStatus: 'all' // 'all', 'open', 'in_progress', 'completed'
  });

  // Estados para integração de API externa
  const [apiIntegration, setApiIntegration] = useState(null);
  const [hasApiIntegration, setHasApiIntegration] = useState(false);
  const [confirmPlate, setConfirmPlate] = useState('');
  const [selectedInfractionCodes, setSelectedInfractionCodes] = useState([]);
  const [infractionCodeInput, setInfractionCodeInput] = useState('');
  const [sendingToExternalApi, setSendingToExternalApi] = useState(false);
  const [showConfirmationPanel, setShowConfirmationPanel] = useState(false);

  const {
    isOpen: isBatchModalOpen,
    onClose: onBatchModalClose
  } = useDisclosure();

  const {
    isOpen: isImageModalOpen,
    onOpen: onImageModalOpen,
    onClose: onImageModalClose
  } = useDisclosure();

  const toast = useToast();

  // Adicionar estados para controle de zoom
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const [showBbox, setShowBbox] = useState(false);
  const [imageNaturalWidth, setImageNaturalWidth] = useState(1);
  const [imageNaturalHeight, setImageNaturalHeight] = useState(1);
  const [plateSuggestionsByInfractionId, setPlateSuggestionsByInfractionId] = useState({});
  const [dismissedPlateSuggestions, setDismissedPlateSuggestions] = useState({});
  const [acceptedPlateCorrections, setAcceptedPlateCorrections] = useState({});
  const originalInfractionStatusRef = useRef({});
  const inFlightSavesRef = useRef(new Map());
  const integrationPayloadsRef = useRef(new Map());
  const pendingPlateCorrectionsRef = useRef(new Map());
  const latestBatchStateRef = useRef(null);
  const loadingInfractionImagesRef = useRef(new Set());
  const loadedImageUrlsRef = useRef(new Set());
  const MAX_BATCH_IMAGE_PRELOAD_CONCURRENCY = 3;
  const tabsItems = useMemo(() => ([
    { label: 'Meus Lotes' },
    { label: 'Lotes Disponíveis' },
    { label: 'Todos os Lotes' },
    { label: 'Em Análise' },
    { label: 'Concluídos' },
    ...(userLevel >= 4 ? [{ label: 'Analítico', icon: ViewIcon }] : [])
  ]), [userLevel]);

  const updateTabsScrollState = useCallback(() => {
    const node = tabsCarouselRef.current;
    if (!node) return;

    const maxLeft = node.scrollWidth - node.clientWidth;
    setCanScrollTabsLeft(node.scrollLeft > 2);
    setCanScrollTabsRight(maxLeft - node.scrollLeft > 2);
  }, []);

  const getTabsStepWidth = useCallback(() => {
    const node = tabsCarouselRef.current;
    if (!node) return 0;

    const firstTab = node.querySelector('[role="tab"]');
    const tabList = node.querySelector('[role="tablist"]');

    if (!firstTab) {
      return node.clientWidth / 3;
    }

    const tabWidth = firstTab.getBoundingClientRect().width;
    const gapValue = tabList ? window.getComputedStyle(tabList).columnGap || window.getComputedStyle(tabList).gap : "0";
    const gap = Number.parseFloat(gapValue) || 0;

    return tabWidth + gap;
  }, []);

  const scrollTabsCarousel = useCallback((direction) => {
    const node = tabsCarouselRef.current;
    if (!node) return;
    const tabStepWidth = getTabsStepWidth() || node.clientWidth / 3;
    node.scrollBy({ left: direction * tabStepWidth, behavior: "smooth" });
    window.requestAnimationFrame(() => {
      window.setTimeout(updateTabsScrollState, 180);
    });
  }, [getTabsStepWidth, updateTabsScrollState]);

  const handleTabsPointerDown = useCallback((event) => {
    const node = tabsCarouselRef.current;
    if (!node) return;

    setIsDraggingTabs(true);
    setTabsDragStartX(event.clientX);
    setTabsScrollStartLeft(node.scrollLeft);
  }, []);

  const handleTabsPointerMove = useCallback((event) => {
    if (!isDraggingTabs) return;
    const node = tabsCarouselRef.current;
    if (!node) return;

    const deltaX = event.clientX - tabsDragStartX;
    node.scrollLeft = tabsScrollStartLeft - deltaX;
  }, [isDraggingTabs, tabsDragStartX, tabsScrollStartLeft]);

  const handleTabsPointerUp = useCallback(() => {
    setIsDraggingTabs(false);
  }, []);

  // Função para aumentar o zoom
  const zoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 4));
  };

  // Função para diminuir o zoom
  const zoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 1));
  };

  // Função para resetar o zoom e a posição
  const resetZoom = () => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  // Função para iniciar arrasto da imagem
  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };
  // Função para arrastar a imagem
  const handleMouseMove = useCallback((e) => {
    if (isDragging && zoomLevel > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart, zoomLevel]);

  // Função para parar o arrasto
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Função para zoom com scroll do mouse
  const handleWheel = (e) => {
    e.preventDefault();

    // Delta Y negativo significa scroll para cima (zoom in)
    // Delta Y positivo significa scroll para baixo (zoom out)
    if (e.deltaY < 0) {
      setZoomLevel(prev => Math.min(prev + 0.1, 4));
    } else {
      setZoomLevel(prev => Math.max(prev - 0.1, 1));
    }
  };  // Efeito para adicionar e remover event listeners globais
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Resetar o zoom quando o modal é aberto ou fechado
  useEffect(() => {
    if (!isImageModalOpen) {
      resetZoom();
      setShowBbox(false);
      setImageNaturalWidth(1);
      setImageNaturalHeight(1);
    }
  }, [isImageModalOpen]);

  // Atualizar a função useEffect inicial
  useEffect(() => {
    fetchBatchStats();
    fetchApiIntegration();
    // Só carregar usuários e algoritmos para usuários de nível superior a 3 (Supervisão e Admin)
    if (userLevel > 3) {
      fetchUsers();
      fetchAlgorithms();
      fetchAnalyticsData();
      const todayDates = getPeriodDates('today');
      if (todayDates) fetchPeriodStats(todayDates.start, todayDates.end);
    }
    fetchCameras();
    fetchBatches('all', false);
    fetchBatches('open', false);
    fetchBatches('in_progress', false);
    fetchBatches('completed', false);
    fetchUserBatches(false);

    // Initialize pendingFilters with the current filters
    setPendingFilters({
      batchId: filters.batchId,
      assigneeId: filters.assigneeId,
      startDate: filters.startDate,
      endDate: filters.endDate,
      cameraId: filters.cameraId,
      algorithmId: filters.algorithmId
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps  
  }, [userLevel]);

  // Efeito para recarregar os lotes quando a ordenação muda
  useEffect(() => {
    // Não executar na primeira renderização
    if (sortOption.initialized) {
      // Recarregar todos os tipos de lotes
      fetchBatches('all', false);
      fetchBatches('open', false);
      fetchBatches('in_progress', false);
      fetchBatches('completed', false);
      fetchUserBatches(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOption]);

  // Update pendingFilters when filters change
  useEffect(() => {
    setPendingFilters({ ...filters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    const maxTabIndex = tabsItems.length - 1;
    if (activeTab > maxTabIndex) {
      setActiveTab(maxTabIndex);
    }
  }, [tabsItems, activeTab]);

  useEffect(() => {
    updateTabsScrollState();
    const handleResize = () => updateTabsScrollState();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [updateTabsScrollState, userLevel]);
// Function to clear all filters
  const clearFilters = async () => {
    // Criar filtros vazios
    const emptyFilters = {
      batchId: '',
      assigneeId: '',
      startDate: '',
      endDate: '',
      cameraId: '',
      algorithmId: '',
      infractionClass: ''
    };

    // Limpar os filtros pendentes
    setPendingFilters(emptyFilters);

    // Limpar os lotes atuais e definir o estado de carregamento
    setBatches(prev => ({
      ...prev,
      all: { ...prev.all, items: [], loading: true, page: 1, hasMore: true },
      open: { ...prev.open, items: [], loading: true, page: 1, hasMore: true },
      inProgress: { ...prev.inProgress, items: [], loading: true, page: 1, hasMore: true },
      completed: { ...prev.completed, items: [], loading: true, page: 1, hasMore: true },
      user: { ...prev.user, items: [], loading: true, page: 1, hasMore: true }
    }));

    // Limpar os filtros atuais
    setFilters(emptyFilters);

    // Certificar-se de que o estado foi atualizado antes de buscar os dados
    await new Promise(resolve => setTimeout(resolve, 300));

    try {
      // Usar Promise.all para garantir que todos os lotes sejam buscados simultaneamente
      await Promise.all([
        fetchBatches('all', false, emptyFilters),
        fetchBatches('open', false, emptyFilters),
        fetchBatches('in_progress', false, emptyFilters),
        fetchBatches('completed', false, emptyFilters),
        fetchUserBatches(false, emptyFilters)
      ]);

      // Notificar o usuário sobre o sucesso
      toast({
        title: 'Filtros limpos',
        description: 'A lista foi atualizada sem filtros',
        status: 'success',
        duration: 2000
      });
    } catch (error) {
      console.error('Erro ao recarregar lotes após limpar filtros:', error);
      toast({
        title: 'Erro ao limpar filtros',
        description: error.message,
        status: 'error',
        duration: 3000
      });
    }
  };
  // Function to apply pending filters
  const applyFilters = async () => {

    // Criar uma cópia local dos filtros pendentes
    const newFilters = { ...pendingFilters };

    // Limpar os lotes atuais e definir o estado de carregamento
    setBatches(prev => ({
      ...prev,
      all: { ...prev.all, items: [], loading: true, page: 1, hasMore: true },
      open: { ...prev.open, items: [], loading: true, page: 1, hasMore: true },
      inProgress: { ...prev.inProgress, items: [], loading: true, page: 1, hasMore: true },
      completed: { ...prev.completed, items: [], loading: true, page: 1, hasMore: true },
      user: { ...prev.user, items: [], loading: true, page: 1, hasMore: true }
    }));

    // Atualizar o estado de filtros e aguardar a conclusão
    setFilters(newFilters);
    setShowFilters(false);

    // Certificar-se de que o estado foi atualizado antes de buscar os dados
    await new Promise(resolve => setTimeout(resolve, 300));

    // Buscar os lotes com os novos filtros sequencialmente para evitar dessincronia
    try {

      // Usar Promise.all para garantir que todos os lotes sejam buscados simultaneamente
      await Promise.all([
        fetchBatches('all', false, newFilters),
        fetchBatches('open', false, newFilters),
        fetchBatches('in_progress', false, newFilters),
        fetchBatches('completed', false, newFilters),
        fetchUserBatches(false, newFilters)
      ]);

      // Notificar o usuário sobre o sucesso
      toast({
        title: 'Filtros aplicados',
        description: 'A lista foi atualizada com os filtros selecionados',
        status: 'success',
        duration: 2000
      });
    } catch (error) {
      console.error('Erro ao buscar lotes com filtros:', error);
      toast({
        title: 'Erro ao aplicar filtros',
        description: error.message,
        status: 'error',
        duration: 3000
      });
    }
  };  // Cliente-side batching filter removed (unused). Reintroduza se necessário.

  // Verificar se existe integração de API externa ativa
  const fetchApiIntegration = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/integrations/check', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      setHasApiIntegration(data.hasIntegration);
      if (data.hasIntegration) {
        setApiIntegration(data.integration);
      }
    } catch (error) {
      console.error('Erro ao verificar integração:', error);
    }
  };

  // Enviar infração para API externa
  const sendInfractionToExternalApi = async (infraction, plate, codes) => {
    if (!apiIntegration) return;

    setSendingToExternalApi(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/integrations/${apiIntegration.id}/send-infraction`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plate: plate,
          infraction_codes: codes,
          infraction_id: infraction.id,
          detected_at: infraction.detected_at,
          camera_name: infraction.Camera?.name || '',
          image_url: infraction.imageUrl || '',
          vehicle_type: infraction.vehicle_type || ''
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao enviar para API externa');
      }

      toast({
        title: 'Enviado para API externa',
        description: `Infração enviada com sucesso para ${apiIntegration.name}`,
        status: 'success',
        duration: 3000
      });

      return data;
    } catch (error) {
      toast({
        title: 'Erro ao enviar para API externa',
        description: error.message,
        status: 'error',
        duration: 5000
      });
      throw error;
    } finally {
      setSendingToExternalApi(false);
    }
  };

  // Adicionar código de infração à lista selecionada
  const addInfractionCode = () => {
    const code = infractionCodeInput.trim();
    if (!code) return;
    if (selectedInfractionCodes.includes(code)) {
      toast({ title: 'Código já adicionado', status: 'warning', duration: 2000 });
      return;
    }
    setSelectedInfractionCodes(prev => [...prev, code]);
    setInfractionCodeInput('');
  };

  // Remover código de infração da lista
  const removeInfractionCode = (code) => {
    setSelectedInfractionCodes(prev => prev.filter(c => c !== code));
  };

  const fetchBatchStats = async () => {
    try {
      const token = localStorage.getItem('token');

      // Verificar o token e obter o nível do usuário
      if (!token) {
        return; // Sem token, não podemos prosseguir
      }

      // Decodificar o token para obter o nível efetivo da sessão
      const decoded = jwtDecode(token);
      const perms = getSessionPermissions(decoded);
      setUserLevel(perms.effectiveRole);
      setUserId(decoded.id || decoded.userId);

      const userRole = perms.effectiveRole;

      // Se o usuário tiver nível 3 ou superior, buscamos as estatísticas
      // OU se o nível não estiver definido, tentamos buscar de qualquer forma
      if (userRole >= 3) {
        const response = await fetch('/api/batches/stats', {
          headers: { 'Authorization': `Bearer ${token}` },
          cache: 'no-cache' // Garantir que não usamos cache
        });

        if (!response.ok) throw new Error('Erro ao buscar estatísticas');

        const data = await response.json();
        setStatistics(data);
      }
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      toast({
        title: 'Erro ao carregar estatísticas',
        description: error.message,
        status: 'error',
        duration: 3000
      });
    }
  };

  // Função para buscar dados analíticos (apenas para usuários nível 4 e 5)
  const fetchAnalyticsData = async () => {
    if (userLevel < 4) return; // Apenas usuários nível 4 e 5

    setLoadingAnalytics(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/api/batches/analytics', {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-cache'
      });

      if (!response.ok) throw new Error('Erro ao buscar dados analíticos');

      const data = await response.json();
      setAnalyticsData(data);
      setAnalyticsLoadedAt(new Date());
    } catch (error) {
      console.error('Erro ao buscar dados analíticos:', error);
      toast({
        title: 'Erro ao carregar analytics',
        description: error.message,
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const fetchPeriodStats = async (startDate, endDate) => {
    if (userLevel < 4) return;
    setLoadingPeriodStats(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await fetch(
        `/api/batches/analytics/period-stats?startDate=${startDate}&endDate=${endDate}`,
        { headers: { 'Authorization': `Bearer ${token}` }, cache: 'no-cache' }
      );
      if (!response.ok) throw new Error('Erro ao buscar estatísticas por período');
      const data = await response.json();
      setPeriodStats(data);
    } catch (error) {
      console.error('Erro ao buscar estatísticas por período:', error);
      toast({
        title: 'Erro ao carregar estatísticas',
        description: error.message,
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoadingPeriodStats(false);
    }
  };

  const getPeriodDates = (period) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (period) {
      case 'today': {
        return { start: today.toISOString().split('T')[0], end: today.toISOString().split('T')[0] };
      }
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        const yStr = yesterday.toISOString().split('T')[0];
        return { start: yStr, end: yStr };
      }
      case 'this_week': {
        const weekStart = new Date(today);
        const dow = today.getDay(); // 0=dom, 1=seg, ...
        const daysSinceMonday = dow === 0 ? 6 : dow - 1;
        weekStart.setDate(today.getDate() - daysSinceMonday);
        return { start: weekStart.toISOString().split('T')[0], end: today.toISOString().split('T')[0] };
      }
      case 'this_month': {
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return { start: monthStart.toISOString().split('T')[0], end: today.toISOString().split('T')[0] };
      }
      default:
        return null;
    }
  };

  const handleSelectPeriod = (period) => {
    setSelectedPeriod(period);
    const dates = getPeriodDates(period);
    if (dates) fetchPeriodStats(dates.start, dates.end);
  };

  const handleApplyCustomPeriod = () => {
    if (!customPeriodStart || !customPeriodEnd) return;
    if (new Date(customPeriodStart) > new Date(customPeriodEnd)) {
      toast({ title: 'Data início não pode ser maior que data fim', status: 'warning', duration: 3000 });
      return;
    }
    setSelectedPeriod('custom');
    fetchPeriodStats(customPeriodStart, customPeriodEnd);
  };

  // Memoized function to process analytics data with filters and sorting
  const processedAnalyticsData = useMemo(() => {
    if (!analyticsData) return analyticsData;

    // Clone data to avoid modifying the original
    const processed = {
      ...analyticsData,
      batchesByCamera: analyticsData.batchesByCamera ? [...analyticsData.batchesByCamera] : [],
      topCameras: analyticsData.topCameras ? [...analyticsData.topCameras] : []
    };

    // Filter by status if not 'all'
    if (analyticsFilters.batchStatus !== 'all' && analyticsData.batchesByCameraAndStatus) {
      // Filter data by selected status
      const filteredByStatus = analyticsData.batchesByCameraAndStatus.filter(
        item => item.status === analyticsFilters.batchStatus
      );

      console.log('DEBUG: Dados filtrados por status', {
        statusSelecionado: analyticsFilters.batchStatus,
        resultsAfterFilter: filteredByStatus.length
      });

      // Group by camera and sum batches
      const cameraMap = {};
      filteredByStatus.forEach(item => {
        if (!cameraMap[item.camera_id]) {
          cameraMap[item.camera_id] = {
            camera_id: item.camera_id,
            camera_name: item.camera_name,
            batch_count: 0
          };
        }
        cameraMap[item.camera_id].batch_count += item.batch_count;
      });

      processed.batchesByCamera = Object.values(cameraMap);
    }

    // Apply threshold filter for batchesByCamera
    if (analyticsFilters.showOnlyAboveThreshold && analyticsFilters.thresholdValue > 0) {
      processed.batchesByCamera = processed.batchesByCamera.filter(
        camera => camera.batch_count >= analyticsFilters.thresholdValue
      );
    }

    // Sort batchesByCamera
    processed.batchesByCamera.sort((a, b) => {
      let compareValue = 0;
      if (analyticsFilters.sortBy === 'count') {
        compareValue = a.batch_count - b.batch_count;
      } else {
        // Sort by name
        compareValue = a.camera_name.localeCompare(b.camera_name);
      }

      return analyticsFilters.sortOrder === 'DESC' ? -compareValue : compareValue;
    });

    // topCameras already comes sorted from backend, but we can apply filter if needed
    if (analyticsFilters.showOnlyAboveThreshold && analyticsFilters.thresholdValue > 0) {
      processed.topCameras = processed.topCameras.filter(
        camera => camera.batch_count >= analyticsFilters.thresholdValue
      );
    }

    return processed;
  }, [analyticsData, analyticsFilters]);

  // Atualizar a função fetchBatches para incluir os novos filtros
  const fetchBatches = async (type, loadMore = false, specificFilters = null) => {
    const stateKey = type === 'in_progress' ? 'inProgress' : type;

    if (loadMore) {
      setBatches(prev => ({
        ...prev,
        [stateKey]: {
          ...prev[stateKey],
          loading: true
        }
      }));
    } else {
      setBatches(prev => ({
        ...prev,
        [stateKey]: {
          ...prev[stateKey],
          loading: true,
          page: 1
        }
      }));
    }

    try {
      const token = localStorage.getItem('token');
      const page = loadMore ? batches[stateKey].page + 1 : 1;
      const sortParam = `&sortField=${sortOption.field}&sortDirection=${sortOption.direction}`;

      // Usar os filtros específicos se fornecidos, caso contrário, usar os filtros do estado
      const currentFilters = specificFilters || { ...filters };

      // Adicionar parâmetros de filtro
      const filterParams = [];

      // Filtro por ID do lote
      if (currentFilters.batchId && currentFilters.batchId.trim() !== '') {
        filterParams.push(`batchId=${currentFilters.batchId.trim()}`);
      }

      // Filtro por ID do assignee (atribuído)
      if (currentFilters.assigneeId && currentFilters.assigneeId.trim() !== '') {
        filterParams.push(`assigneeId=${currentFilters.assigneeId.trim()}`);
      }

      // Filtros de data
      if (currentFilters.startDate && currentFilters.startDate.trim() !== '') {
        filterParams.push(`startDate=${currentFilters.startDate.trim()}`);
      }

      if (currentFilters.endDate && currentFilters.endDate.trim() !== '') {
        filterParams.push(`endDate=${currentFilters.endDate.trim()}`);
      }

      // Filtro por câmera
      if (currentFilters.cameraId && currentFilters.cameraId.trim() !== '') {
        filterParams.push(`cameraId=${currentFilters.cameraId.trim()}`);
      }

      // Filtro por algoritmo
      if (currentFilters.algorithmId && currentFilters.algorithmId.trim() !== '') {
        filterParams.push(`algorithmId=${currentFilters.algorithmId.trim()}`);
      }

      // Filtro por tipo de infração
      if (currentFilters.infractionClass && currentFilters.infractionClass.trim() !== '') {
        filterParams.push(`infractionClass=${encodeURIComponent(currentFilters.infractionClass.trim())}`);
      }

      // Montar os parâmetros de filtro
      const filterParam = filterParams.length > 0 ? `&${filterParams.join('&')}` : '';

      // Determinar qual endpoint usar com base nos filtros
      let apiUrl;

      // Se há filtros, usar a rota dedicada de filtro
      if ((currentFilters.batchId && currentFilters.batchId.trim() !== '') ||
        (currentFilters.assigneeId && currentFilters.assigneeId.trim() !== '') ||
        (currentFilters.startDate && currentFilters.startDate.trim() !== '') ||
        (currentFilters.endDate && currentFilters.endDate.trim() !== '') ||
        (currentFilters.cameraId && currentFilters.cameraId.trim() !== '') ||
        (currentFilters.algorithmId && currentFilters.algorithmId.trim() !== '') ||
        (currentFilters.infractionClass && currentFilters.infractionClass.trim() !== '')) {

        // Se não for da aba "Todos", adicionar filtro de status
        const statusParam = type !== 'all' ? `&status=${type}` : '';

        // Usar a rota dedicada de filtro no backend
        apiUrl = `/api/batches/filter?page=${page}&limit=12${sortParam}${filterParam}${statusParam}`;
      } else {
        // Caso contrário, usar a rota padrão
        const statusParam = type !== 'all' ? `&status=${type}` : '';
        apiUrl = `/api/batches?page=${page}&limit=12${statusParam}${sortParam}`;
      }

      const response = await fetch(apiUrl, {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-cache' // Evitar problemas com cache
      });

      if (!response.ok) throw new Error('Erro ao buscar lotes');

      const data = await response.json();
      // Verificar se data.batches existe e é um array
      const batchesArray = Array.isArray(data.batches) ? data.batches : [];

      const filteredBatches = batchesArray;

      setBatches(prev => ({
        ...prev,
        [stateKey]: {
          items: loadMore
            ? [...prev[stateKey].items, ...filteredBatches]
            : filteredBatches,
          loading: false,
          page: page,
          hasMore: page < data.totalPages && filteredBatches.length > 0
        }
      }));
    } catch (error) {
      console.error(`Erro ao buscar lotes ${type}:`, error);
      setBatches(prev => ({
        ...prev,
        [stateKey]: {
          ...prev[stateKey],
          loading: false
        }
      }));

      toast({
        title: 'Erro ao carregar lotes',
        description: error.message,
        status: 'error',
        duration: 3000
      });
    }
  };

  // Função para buscar câmeras
  const fetchCameras = async () => {
    setLoadingCameras(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/cameras', {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-cache'
      });

      if (!response.ok) throw new Error('Erro ao buscar câmeras');

      const data = await response.json();
      setCameras(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao buscar câmeras:', error);
      toast({
        title: 'Erro ao carregar câmeras para filtro',
        description: error.message,
        status: 'error',
        duration: 3000
      });
      setCameras([]);
    } finally {
      setLoadingCameras(false);
    }
  };

  const fetchAlgorithms = async () => {
    setLoadingAlgorithms(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/algorithms', {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-cache'
      });

      if (!response.ok) throw new Error('Erro ao buscar algoritmos');

      const data = await response.json();
      setAlgorithms(filterAllowedAlgorithms(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Erro ao buscar algoritmos:', error);
      toast({
        title: 'Erro ao carregar algoritmos para filtro',
        description: error.message,
        status: 'error',
        duration: 3000
      });
      setAlgorithms([]);
    } finally {
      setLoadingAlgorithms(false);
    }
  };

  const fetchUserBatches = async (loadMore = false, specificFilters = null) => {
    if (loadMore) {
      setBatches(prev => ({
        ...prev,
        user: {
          ...prev.user,
          loading: true
        }
      }));
    } else {
      setBatches(prev => ({
        ...prev,
        user: {
          ...prev.user,
          loading: true,
          page: 1
        }
      }));
    }

    try {
      const token = localStorage.getItem('token');
      const page = loadMore ? batches.user.page + 1 : 1;
      const sortParam = `&sortField=${sortOption.field}&sortDirection=${sortOption.direction}`;

      // Usar os filtros específicos se fornecidos, caso contrário, usar os filtros do estado
      const currentFilters = specificFilters || { ...filters };

      // Adicionar todos os parâmetros de filtro incluindo assigneeId
      const filterParams = [];

      // Filtros de data
      if (currentFilters.startDate && currentFilters.startDate.trim() !== '') {
        filterParams.push(`startDate=${currentFilters.startDate.trim()}`);
      }

      if (currentFilters.endDate && currentFilters.endDate.trim() !== '') {
        filterParams.push(`endDate=${currentFilters.endDate.trim()}`);
      }

      const filterParam = filterParams.length > 0 ? `&${filterParams.join('&')}` : '';

      // Determinar qual endpoint usar com base nos filtros
      let apiUrl;
      // Se há filtros de data, usar a rota dedicada de filtro
      if ((currentFilters.startDate && currentFilters.startDate.trim() !== '') ||
        (currentFilters.endDate && currentFilters.endDate.trim() !== '')) {

        // Para a rota de filtro, precisamos passar o ID do usuário atual
        const userId = localStorage.getItem('userId');

        // Verificar se o ID do usuário é válido e não é "null"
        const assigneeParam = userId && userId !== "null" ? `&assigneeId=${userId}` : '';

        // Usar a rota dedicada de filtro no backend e incluir o assigneeId se disponível
        apiUrl = `/api/batches/filter?page=${page}&limit=12${sortParam}${filterParam}${assigneeParam}`;
      } else {
        // Caso contrário, usar a rota padrão para lotes do usuário
        apiUrl = `/api/batches/user?page=${page}&limit=12${sortParam}`;
      }

      const response = await fetch(apiUrl, {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-cache' // Evitar problemas com cache
      });

      if (!response.ok) throw new Error('Erro ao buscar lotes do usuário');

      const data = await response.json();

      // Log detalhado para debug
      const batchesArray = data.batches || [];

      // Como estamos usando a rota de filtro dedicada, não precisamos aplicar filtros de data no cliente
      const filteredBatches = batchesArray;

      setBatches(prev => ({
        ...prev,
        user: {
          items: loadMore
            ? [...prev.user.items, ...filteredBatches]
            : filteredBatches,
          loading: false,
          page: page,
          hasMore: loadMore && filteredBatches.length > 0 && page < data.totalPages
        }
      }));
    } catch (error) {
      console.error('Erro ao buscar lotes do usuário:', error);
      setBatches(prev => ({
        ...prev,
        user: {
          ...prev.user,
          loading: false
        }
      }));

      toast({
        title: 'Erro ao carregar seus lotes',
        description: error.message,
        status: 'error',
        duration: 3000
      });
    }
  };

  const createBatches = async () => {
    setCreatingBatches(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/batches/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Erro ao criar lotes');

      const data = await response.json();

      toast({
        title: 'Lotes criados com sucesso',
        description: data.message,
        status: 'success',
        duration: 3000
      });

      // Atualizar dados
      fetchBatchStats();
      fetchBatches('all');
      fetchBatches('open');
    } catch (error) {
      console.error('Erro ao criar lotes:', error);
      toast({
        title: 'Erro ao criar lotes',
        description: error.message,
        status: 'error',
        duration: 3000
      });
    } finally {
      setCreatingBatches(false);
    }
  };
  const assignBatch = async (batchId) => {
    setLoadingReviewBatchId(batchId);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/batches/${batchId}/assign`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao atribuir lote');
      }

      await response.json();

      toast({
        title: 'Lote atribuído com sucesso',
        description: 'Você agora pode começar a análise das infrações',
        status: 'success',
        duration: 3000
      });

      fetchBatchStats();
      fetchBatches('all');
      fetchBatches('open');
      fetchUserBatches();

      await openBatchDetails(batchId);
    } catch (error) {
      console.error('Erro ao atribuir lote:', error);
      toast({
        title: 'Erro ao atribuir lote',
        description: error.message,
        status: 'error',
        duration: 5000
      });
      setLoadingReviewBatchId(null);
    }
  };

  // Função para desvincular um lote (apenas admin)
  const unassignBatch = async (batchId) => {
    setLoadingUnassignBatchId(batchId);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/batches/${batchId}/unassign`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao desvincular lote');
      }

      await response.json();

      toast({
        title: 'Lote desvinculado com sucesso',
        description: 'O lote foi retornado para a lista de lotes disponíveis',
        status: 'success',
        duration: 3000
      });

      // Atualizar dados
      fetchBatchStats();
      fetchBatches('all');
      fetchBatches('open');
      fetchBatches('in_progress');
      fetchUserBatches();
    } catch (error) {
      console.error('Erro ao desvincular lote:', error);
      toast({
        title: 'Erro ao desvincular lote',
        description: error.message,
        status: 'error',
        duration: 5000
      });
    } finally {
      setLoadingUnassignBatchId(null);
    }
  };

  // Função para pegar o lote mais antigo disponível
  const assignRandomBatch = async () => {
    setLoadingReviewBatchId('random');
    try {
      const token = localStorage.getItem('token');
      // Buscar lotes disponíveis ordenados por data de criação (mais antigos primeiro)
      const response = await fetch('/api/batches?status=open&limit=100&sortField=created_at&sortDirection=ASC', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erro ao buscar lotes disponíveis');
      const data = await response.json();
      const batchesArray = Array.isArray(data.batches) ? data.batches : [];
      if (batchesArray.length === 0) throw new Error('Nenhum lote disponível');

      // Pegar o primeiro lote (mais antigo)
      const oldestBatch = batchesArray[0];
      await assignBatch(oldestBatch.id);
    } catch (error) {
      toast({
        title: 'Erro ao atribuir lote',
        description: error.message,
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoadingReviewBatchId(null);
    }
  };

  const revokeInfractionImageUrls = useCallback((items = []) => {
    items.forEach((infraction) => {
      if (infraction?.imageUrl && loadedImageUrlsRef.current.has(infraction.imageUrl)) {
        URL.revokeObjectURL(infraction.imageUrl);
        loadedImageUrlsRef.current.delete(infraction.imageUrl);
      }
    });
  }, []);

  const getLocalAnalyzedCount = useCallback((items = []) => (
    items.filter((infraction) => infraction.status !== 'pending').length
  ), []);

  const syncPendingReviewsState = useCallback(() => {
    setHasUnsavedReviews(inFlightSavesRef.current.size > 0);
  }, []);

  const fetchImageWithAuth = async (filename) => {
    if (!filename) return null;

    const actualFilename = toStorageImageApiSegment(filename, 'infractions');
    if (!actualFilename) return null;

    const token = localStorage.getItem('token');
    const response = await fetch(`/api/infractions/image/${actualFilename}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (!response.ok) throw new Error('Erro ao buscar imagem');

    const blob = await response.blob();
    const imageUrl = URL.createObjectURL(blob);
    loadedImageUrlsRef.current.add(imageUrl);
    return imageUrl;
  };

  const enrichInfractionsWithPlates = async (infractionsList) => {
    try {
      const token = localStorage.getItem('token');
      const platesResp = await fetch('/api/plates/lookup', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const platesList = platesResp.ok ? await platesResp.json() : [];
      const platesMap = {};
      (Array.isArray(platesList) ? platesList : []).forEach((plateRecord) => {
        const key = (plateRecord.plateNumber || plateRecord.plate || '').toUpperCase();
        if (key) platesMap[key] = plateRecord;
      });

      return infractionsList.map((infraction) => {
        const key = (infraction.plate || '').toUpperCase();
        const plateRec = platesMap[key];
        const registeredVehicleType = plateRec ? (plateRec.vehicleType || null) : null;
        const vehicle_type = (!infraction.vehicle_type || infraction.vehicle_type === 'DESCONHECIDO') && registeredVehicleType
          ? registeredVehicleType
          : infraction.vehicle_type;

        return {
          ...infraction,
          imageUrl: null,
          imageLoading: Boolean(infraction.image_path),
          registeredVehicleType,
          vehicle_type
        };
      });
    } catch (error) {
      console.error('Erro ao enriquecer infrações com placas:', error);
      return infractionsList.map((infraction) => ({
        ...infraction,
        imageUrl: null,
        imageLoading: Boolean(infraction.image_path)
      }));
    }
  };

  const loadPlateSuggestionsForInfractions = useCallback(async (infractionsList) => {
    try {
      const suggestionsByPlate = await fetchPlateSuggestions(
        infractionsList.map((infraction) => infraction.plate)
      );

      const suggestionsByInfractionId = {};
      infractionsList.forEach((infraction) => {
        const normalizedPlate = String(infraction.plate || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
        const suggestion = suggestionsByPlate[normalizedPlate];
        if (suggestion) {
          suggestionsByInfractionId[infraction.id] = suggestion;
        }
      });

      setPlateSuggestionsByInfractionId(suggestionsByInfractionId);
      setDismissedPlateSuggestions({});
      setAcceptedPlateCorrections({});
      pendingPlateCorrectionsRef.current = new Map();
    } catch (error) {
      console.error('Erro ao buscar sugestões de placa:', error);
      setPlateSuggestionsByInfractionId({});
    }
  }, []);

  const applyPlateCorrectionLocally = useCallback((infractionId, suggestion) => {
    const applyToInfraction = (infraction) => (
      infraction.id === infractionId
        ? {
          ...infraction,
          plate: suggestion.plateNumber,
          registeredVehicleType: suggestion.vehicleType || null,
          vehicle_type: suggestion.vehicleType || infraction.vehicle_type
        }
        : infraction
    );

    setInfractions((current) => current.map(applyToInfraction));
    setSelectedInfraction((current) => (
      current?.id === infractionId ? applyToInfraction(current) : current
    ));
  }, []);

  const handleAcceptPlateSuggestion = useCallback((infractionId, suggestion) => {
    pendingPlateCorrectionsRef.current.set(infractionId, {
      plate: suggestion.plateNumber,
      vehicleType: suggestion.vehicleType || null
    });

    setAcceptedPlateCorrections((current) => ({
      ...current,
      [infractionId]: suggestion
    }));

    setPlateSuggestionsByInfractionId((current) => {
      const next = { ...current };
      delete next[infractionId];
      return next;
    });

    applyPlateCorrectionLocally(infractionId, suggestion);

    setConfirmPlate((current) => (
      selectedInfraction?.id === infractionId ? suggestion.plateNumber : current
    ));

    const existingPayload = integrationPayloadsRef.current.get(infractionId);
    if (existingPayload) {
      integrationPayloadsRef.current.set(infractionId, {
        ...existingPayload,
        plate: suggestion.plateNumber
      });
    }
  }, [applyPlateCorrectionLocally, selectedInfraction?.id]);

  const handleRejectPlateSuggestion = useCallback((infractionId) => {
    setDismissedPlateSuggestions((current) => ({
      ...current,
      [infractionId]: true
    }));

    setPlateSuggestionsByInfractionId((current) => {
      const next = { ...current };
      delete next[infractionId];
      return next;
    });
  }, []);

  const getActivePlateSuggestion = useCallback((infraction) => {
    if (!infraction?.id) return null;
    if (acceptedPlateCorrections[infraction.id] || dismissedPlateSuggestions[infraction.id]) {
      return null;
    }
    return plateSuggestionsByInfractionId[infraction.id] || null;
  }, [acceptedPlateCorrections, dismissedPlateSuggestions, plateSuggestionsByInfractionId]);

  const applyInfractionImageUpdate = useCallback((infractionId, imageUrl) => {
    setInfractions((current) => current.map((infraction) => (
      infraction.id === infractionId
        ? { ...infraction, imageUrl, imageLoading: false }
        : infraction
    )));

    setSelectedInfraction((current) => (
      current?.id === infractionId
        ? { ...current, imageUrl, imageLoading: false }
        : current
    ));
  }, []);

  const loadInfractionImage = useCallback(async (infraction) => {
    if (!infraction?.image_path || infraction.imageUrl) {
      return infraction;
    }

    if (loadingInfractionImagesRef.current.has(infraction.id)) {
      return infraction;
    }

    loadingInfractionImagesRef.current.add(infraction.id);
    setInfractions((current) => current.map((item) => (
      item.id === infraction.id ? { ...item, imageLoading: true } : item
    )));
    setSelectedInfraction((current) => (
      current?.id === infraction.id ? { ...current, imageLoading: true } : current
    ));

    try {
      const imageUrl = await fetchImageWithAuth(infraction.image_path);
      applyInfractionImageUpdate(infraction.id, imageUrl);
      return { ...infraction, imageUrl, imageLoading: false };
    } catch (error) {
      console.error('Erro ao carregar imagem:', error);
      applyInfractionImageUpdate(infraction.id, null);
      return { ...infraction, imageUrl: null, imageLoading: false };
    } finally {
      loadingInfractionImagesRef.current.delete(infraction.id);
    }
  }, [applyInfractionImageUpdate]);

  const preloadRemainingBatchImages = useCallback(async (infractionsList, skipInfractionId) => {
    const pendingLoads = infractionsList.filter((infraction) => (
      infraction.id !== skipInfractionId
      && infraction.image_path
      && !infraction.imageUrl
    ));

    if (pendingLoads.length === 0) {
      setBackgroundImagesLoading(false);
      setBackgroundImagesLoaded(0);
      setBackgroundImagesTotal(0);
      return;
    }

    setBackgroundImagesLoading(true);
    setBackgroundImagesLoaded(0);
    setBackgroundImagesTotal(pendingLoads.length);

    let loadedCount = 0;
    let cursor = 0;

    const worker = async () => {
      while (cursor < pendingLoads.length) {
        const currentIndex = cursor;
        cursor += 1;
        const infraction = pendingLoads[currentIndex];
        await loadInfractionImage(infraction);
        loadedCount += 1;
        setBackgroundImagesLoaded(loadedCount);
      }
    };

    const workers = Array.from(
      { length: Math.min(MAX_BATCH_IMAGE_PRELOAD_CONCURRENCY, pendingLoads.length) },
      () => worker()
    );

    await Promise.all(workers);
    setBackgroundImagesLoading(false);
  }, [loadInfractionImage]);

  const openBatchDetails = async (batchId) => {
    setLoadingReviewBatchId(batchId);
    setIsLoadingBatchModal(true);
    setBatchLoadingMessage('Carregando informações do lote...');

    try {
      const token = localStorage.getItem('token');

      const batchResponse = await fetch(`/api/batches/${batchId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!batchResponse.ok) throw new Error('Erro ao buscar detalhes do lote');

      const batchData = await batchResponse.json();
      setSelectedBatch(batchData);
      latestBatchStateRef.current = batchData;

      let canEdit = true;
      if (userLevel >= 4) {
        canEdit = batchData.assignee_id === userId;
      } else if (userLevel === 2) {
        canEdit = batchData.assignee_id === userId;
      } else if (userLevel < 2) {
        canEdit = false;
      }

      setCanEditBatch(canEdit);

      const infractionsResponse = await fetch(`/api/batches/${batchId}/infractions?limit=1000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!infractionsResponse.ok) throw new Error('Erro ao buscar infrações do lote');

      const infractionsData = await infractionsResponse.json();

      setSelectedBatch((prev) => ({
        ...prev,
        total_infractions: infractionsData.batchInfo.total_infractions,
        analyzed_infractions: infractionsData.batchInfo.analyzed_infractions
      }));

      const sortedInfractions = infractionsData.infractions.sort((a, b) =>
        new Date(a.detected_at) - new Date(b.detected_at)
      );

      const enrichedInfractions = await enrichInfractionsWithPlates(sortedInfractions);
      await loadPlateSuggestionsForInfractions(enrichedInfractions);
      const pendingIndex = enrichedInfractions.findIndex((inf) => inf.status === 'pending');
      const selectedIndex = pendingIndex >= 0 ? pendingIndex : 0;
      const firstInfraction = enrichedInfractions[selectedIndex];

      originalInfractionStatusRef.current = Object.fromEntries(
        enrichedInfractions.map((infraction) => [infraction.id, infraction.status])
      );
      inFlightSavesRef.current = new Map();
      integrationPayloadsRef.current = new Map();
      syncPendingReviewsState();

      setInfractions(enrichedInfractions);
      setCurrentInfractionIndex(selectedIndex);
      setSelectedInfraction(firstInfraction);

      setBatchLoadingMessage('Carregando primeira foto...');
      if (firstInfraction?.image_path) {
        await loadInfractionImage(firstInfraction);
      }

      setIsLoadingBatchModal(false);
      onImageModalOpen();

      preloadRemainingBatchImages(enrichedInfractions, firstInfraction?.id);
    } catch (error) {
      console.error('Erro ao abrir detalhes do lote:', error);
      setIsLoadingBatchModal(false);
      toast({
        title: 'Erro ao carregar detalhes',
        description: error.message,
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoadingInfractions(false);
      setLoadingReviewBatchId(null);
    }
  };

  const waitForPendingSaves = useCallback(async () => {
    const promises = Array.from(inFlightSavesRef.current.values());
    if (promises.length === 0) return;
    await Promise.allSettled(promises);
  }, []);

  const saveInfractionReviewInBackground = useCallback((infractionId, status, batchId) => {
    if (!['confirmed', 'rejected'].includes(status)) return null;

    const token = localStorage.getItem('token');
    const savePromise = fetch(`/api/batches/${batchId}/infractions/${infractionId}/review`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status })
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Erro ao salvar validação');
        }
        return response.json();
      })
      .then((data) => {
        originalInfractionStatusRef.current[infractionId] = status;

        setInfractions((current) => current.map((infraction) => (
          infraction.id === infractionId
            ? { ...infraction, ...data.infraction }
            : infraction
        )));

        setSelectedInfraction((current) => (
          current?.id === infractionId
            ? { ...current, ...data.infraction }
            : current
        ));

        setSelectedBatch((prev) => {
          if (!prev || prev.id !== batchId) return prev;
          const next = {
            ...prev,
            analyzed_infractions: data.batch?.analyzed_infractions ?? prev.analyzed_infractions,
            total_infractions: data.batch?.total_infractions ?? prev.total_infractions,
            status: data.batch?.status ?? prev.status,
            completed_at: data.batch?.completed_at ?? prev.completed_at
          };
          latestBatchStateRef.current = next;
          return next;
        });

        return data;
      })
      .catch((error) => {
        const originalStatus = originalInfractionStatusRef.current[infractionId] ?? 'pending';

        setInfractions((current) => {
          const reverted = current.map((infraction) => (
            infraction.id === infractionId
              ? { ...infraction, status: originalStatus }
              : infraction
          ));

          setSelectedBatch((prev) => {
            if (!prev || prev.id !== batchId) return prev;
            return {
              ...prev,
              analyzed_infractions: getLocalAnalyzedCount(reverted)
            };
          });

          return reverted;
        });

        setSelectedInfraction((current) => (
          current?.id === infractionId
            ? { ...current, status: originalStatus }
            : current
        ));

        toast({
          title: 'Erro ao salvar validação',
          description: error.message,
          status: 'error',
          duration: 4000
        });

        throw error;
      })
      .finally(() => {
        inFlightSavesRef.current.delete(infractionId);
        syncPendingReviewsState();
      });

    inFlightSavesRef.current.set(infractionId, savePromise);
    syncPendingReviewsState();

    return savePromise;
  }, [getLocalAnalyzedCount, syncPendingReviewsState, toast]);

  const sendPendingIntegrations = useCallback(async () => {
    if (!hasApiIntegration || !apiIntegration) return;

    const entries = Array.from(integrationPayloadsRef.current.entries());
    if (entries.length === 0) return;

    for (const [infractionId, payload] of entries) {
      const infraction = infractions.find((item) => item.id === Number(infractionId));
      if (!infraction || infraction.status !== 'confirmed') continue;

      try {
        await sendInfractionToExternalApi(infraction, payload.plate, payload.codes);
      } catch {
        // Erro já tratado no toast da função de envio
      }
    }

    integrationPayloadsRef.current = new Map();
  }, [apiIntegration, hasApiIntegration, infractions]);

  // Wrapper para fechar o modal de detalhes do lote e resetar estados
  const handleCloseBatchModal = useCallback(() => {
    setCanEditBatch(true);
    setSelectedBatch(null);
    setInfractions([]);
    setCurrentInfractionIndex(0);
    setSelectedInfraction(null);
    onBatchModalClose();
  }, [onBatchModalClose]);

  const closeImageReviewModal = useCallback(() => {
    revokeInfractionImageUrls(infractions);
    onImageModalClose();
    handleCloseBatchModal();
    setShowConfirmationPanel(false);
    setBackgroundImagesLoading(false);
    setBackgroundImagesLoaded(0);
    setBackgroundImagesTotal(0);
    inFlightSavesRef.current = new Map();
    integrationPayloadsRef.current = new Map();
    pendingPlateCorrectionsRef.current = new Map();
    setPlateSuggestionsByInfractionId({});
    setDismissedPlateSuggestions({});
    setAcceptedPlateCorrections({});
    originalInfractionStatusRef.current = {};
    latestBatchStateRef.current = null;
    syncPendingReviewsState();
  }, [
    handleCloseBatchModal,
    infractions,
    onImageModalClose,
    revokeInfractionImageUrls,
    syncPendingReviewsState
  ]);

  const handleCloseImageReviewModal = useCallback(async () => {
    if (inFlightSavesRef.current.size > 0 && selectedBatch && canEditBatch) {
      setSubmittingBatchReviews(true);
      try {
        await waitForPendingSaves();
      } catch (error) {
        toast({
          title: 'Erro ao salvar validações',
          description: error.message,
          status: 'error',
          duration: 4000
        });
        return;
      } finally {
        setSubmittingBatchReviews(false);
      }
    }

    closeImageReviewModal();
  }, [
    canEditBatch,
    closeImageReviewModal,
    selectedBatch,
    toast,
    waitForPendingSaves
  ]);

  const reviewInfraction = (status, skipIntegration = false) => {
    if (!selectedBatch || !selectedInfraction || !canEditBatch) return;
    if (!['confirmed', 'rejected', 'pending'].includes(status)) return;

    if (selectedInfraction.status === status) {
      return;
    }

    if (status === 'confirmed' && hasApiIntegration && apiIntegration?.hasRegisterRoute && !skipIntegration && !showConfirmationPanel) {
      setConfirmPlate(selectedInfraction.plate || '');
      setSelectedInfractionCodes([]);
      setInfractionCodeInput('');
      setShowConfirmationPanel(true);
      return;
    }

    const wasStatusChange = selectedInfraction.status !== 'pending';
    const originalStatus = originalInfractionStatusRef.current[selectedInfraction.id] ?? selectedInfraction.status;

    if (status === 'confirmed' && skipIntegration && hasApiIntegration) {
      integrationPayloadsRef.current.set(selectedInfraction.id, {
        plate: confirmPlate,
        codes: [...selectedInfractionCodes]
      });
      setShowConfirmationPanel(false);
      setConfirmPlate('');
      setSelectedInfractionCodes([]);
    }

    if (status === 'rejected') {
      integrationPayloadsRef.current.delete(selectedInfraction.id);
      setShowConfirmationPanel(false);
    }

    const updatedInfractions = infractions.map((infraction) => (
      infraction.id === selectedInfraction.id
        ? { ...infraction, status }
        : infraction
    ));

    setInfractions(updatedInfractions);
    setSelectedInfraction((current) => ({ ...current, status }));
    setSelectedBatch((current) => {
      const next = {
        ...current,
        analyzed_infractions: getLocalAnalyzedCount(updatedInfractions)
      };
      latestBatchStateRef.current = next;
      return next;
    });

    if (status !== originalStatus && status !== 'pending') {
      saveInfractionReviewInBackground(selectedInfraction.id, status, selectedBatch.id);
    }

    if (!wasStatusChange) {
      goToNextPendingInfraction(updatedInfractions);
    }
  };

  const completeBatch = async () => {
    if (!selectedBatch) return;

    if (infractions.some((infraction) => infraction.status === 'pending')) {
      toast({
        title: 'Lote incompleto',
        description: 'Analise todas as infrações pendentes antes de finalizar o lote.',
        status: 'warning',
        duration: 3500
      });
      return;
    }

    setSubmittingBatchReviews(true);

    try {
      await waitForPendingSaves();
      await sendPendingIntegrations();

      let batchData = latestBatchStateRef.current;

      if (batchData?.status !== 'completed') {
        const token = localStorage.getItem('token');
        const plateCorrections = Array.from(pendingPlateCorrectionsRef.current.entries()).map(([id, data]) => ({
          id: Number(id),
          plate: data.plate
        }));

        const response = await fetch(`/api/batches/${selectedBatch.id}/complete`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ plate_corrections: plateCorrections })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Erro ao finalizar lote');
        }

        const data = await response.json();
        batchData = data.batch;
      }

      const confirmadas = infractions.filter((inf) => inf.status === 'confirmed').length;
      const rejeitadas = infractions.filter((inf) => inf.status === 'rejected').length;

      setSelectedBatch((prev) => ({
        ...prev,
        status: 'completed',
        completed_at: batchData?.completed_at ?? prev.completed_at
      }));

      toast({
        title: 'Lote finalizado',
        description: `${confirmadas} confirmadas • ${rejeitadas} rejeitadas`,
        status: 'success',
        duration: 3000
      });

      closeImageReviewModal();

      setTimeout(() => {
        fetchBatchStats();
        fetchBatches('all');
        fetchUserBatches();
        fetchBatches('completed');
      }, 1000);
    } catch (error) {
      console.error('Erro ao finalizar lote:', error);
      toast({
        title: 'Erro ao finalizar lote',
        description: error.message,
        status: 'error',
        duration: 3000
      });
    } finally {
      setSubmittingBatchReviews(false);
    }
  };

  const goToNextPendingInfraction = (sourceInfractions) => {
    const list = Array.isArray(sourceInfractions) ? sourceInfractions : infractions;
    const currentIndex = selectedInfraction
      ? list.findIndex((inf) => inf.id === selectedInfraction.id)
      : currentInfractionIndex;
    const baseIndex = currentIndex >= 0 ? currentIndex : currentInfractionIndex;

    const nextPendingIndex = list.findIndex(
      (inf, idx) => idx > baseIndex && inf.status === 'pending'
    );

    if (nextPendingIndex >= 0) {
      setCurrentInfractionIndex(nextPendingIndex);
      const nextInfraction = list[nextPendingIndex];
      setSelectedInfraction(nextInfraction);
      loadInfractionImage(nextInfraction);
      return;
    }

    const firstPendingIndex = list.findIndex((inf) => inf.status === 'pending');

    if (firstPendingIndex >= 0) {
      setCurrentInfractionIndex(firstPendingIndex);
      const nextInfraction = list[firstPendingIndex];
      setSelectedInfraction(nextInfraction);
      loadInfractionImage(nextInfraction);
      return;
    }

    toast({
      title: 'Nenhuma infração pendente',
      description: 'Todas as infrações deste lote foram classificadas.',
      status: 'info',
      duration: 3000
    });
  };

  const goToPreviousInfraction = () => {
    if (currentInfractionIndex > 0) {
      const previousIndex = currentInfractionIndex - 1;
      const previousInfraction = infractions[previousIndex];
      setCurrentInfractionIndex(previousIndex);
      setSelectedInfraction(previousInfraction);
      setShowConfirmationPanel(false);
      loadInfractionImage(previousInfraction);
    }
  };

  const goToNextInfraction = () => {
    if (currentInfractionIndex < infractions.length - 1) {
      const nextIndex = currentInfractionIndex + 1;
      const nextInfraction = infractions[nextIndex];
      setCurrentInfractionIndex(nextIndex);
      setSelectedInfraction(nextInfraction);
      setShowConfirmationPanel(false);
      loadInfractionImage(nextInfraction);
    }
  };

  // Handle keyboard shortcuts when image modal is open (C/R/P arrows X/Esc)
  const handleImageModalKeyDown = (e) => {
    if (!isImageModalOpen || !selectedInfraction || submittingBatchReviews || !canEditBatch) return;
    const key = e.key.toLowerCase();
    switch (key) {
      case 'c':
        reviewInfraction('confirmed');
        break;
      case 'r':
        reviewInfraction('rejected');
        break;
      case 'p':
        reviewInfraction('pending');
        break;
      case 'arrowright':
        goToNextInfraction();
        break;
      case 'arrowleft':
        goToPreviousInfraction();
        break;
      case 'x':
      case 'escape':
        handleCloseImageReviewModal();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isImageModalOpen) document.addEventListener('keydown', handleImageModalKeyDown);
    return () => document.removeEventListener('keydown', handleImageModalKeyDown);
  }, [isImageModalOpen, selectedInfraction, currentInfractionIndex, submittingBatchReviews, infractions.length, canEditBatch, handleCloseImageReviewModal]);

  const refreshData = () => {
    fetchBatchStats();
    fetchBatches('all', false);
    fetchBatches('open', false);
    fetchBatches('in_progress', false);
    fetchBatches('completed', false);
    fetchUserBatches(false);
    if (userLevel >= 4) {
      fetchAnalyticsData();
      const dates = getPeriodDates(selectedPeriod === 'custom' ? null : selectedPeriod);
      if (dates) fetchPeriodStats(dates.start, dates.end);
      else if (selectedPeriod === 'custom' && customPeriodStart && customPeriodEnd) {
        fetchPeriodStats(customPeriodStart, customPeriodEnd);
      }
    }

    toast({
      title: 'Dados atualizados',
      status: 'success',
      duration: 2000
    });
  };

  // Função para buscar a lista de usuários para o filtro
  // Corrigir a função fetchUsers - remover a condição userLevel
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('token');

      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-cache' // Evitar problemas com cache
      });

      if (!response.ok) throw new Error('Erro ao buscar usuários');

      const data = await response.json();

      // A API retorna diretamente um array de usuários, não um objeto {users: [...]}
      if (Array.isArray(data)) {
        setUsers(data);
      } else if (data.users && Array.isArray(data.users)) {
        // Caso a API mude no futuro para retornar um objeto {users: [...]}
        setUsers(data.users);
      } else {
        console.error('Formato de dados de usuários inesperado:', data);
        setUsers([]);
      }

    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      toast({
        title: 'Erro ao carregar usuários para filtro',
        description: error.message,
        status: 'error',
        duration: 3000
      });
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const renderInfractionDetails = () => {
    if (!selectedInfraction) return null;

    const detectedDate = formatDateWithWeekday(selectedInfraction.detected_at);
    const reviewedDate = selectedInfraction.reviewed_at ? formatDateWithWeekday(selectedInfraction.reviewed_at) : null;

    return (
      <Box>
        {!canEditBatch && userLevel >= 4 && (
          <Box bg="blue.50" p={3} borderRadius="md" mb={4} borderLeft="4px solid" borderLeftColor="blue.400">
            <Text fontSize="sm" color="blue.700" fontWeight="medium">
              📋 Visualização apenas - Este lote foi atribuído a outro usuário. Você pode visualizar as infrações mas não pode classificá-las.
            </Text>
          </Box>
        )}
        <Flex justify="space-between" mb={2}>
          <Text fontSize="sm" fontWeight="bold">
            ID: {selectedInfraction.id}
          </Text>
          <Badge colorScheme={getInfractionStatusColor(selectedInfraction.status)}>
            {getInfractionStatusText(selectedInfraction.status)}
          </Badge>
        </Flex>

        <Box position="relative" mb={4} borderRadius="md" overflow="hidden" borderWidth="1px">
          {selectedInfraction.imageUrl ? (
            <Image
              src={selectedInfraction.imageUrl}
              alt="Imagem da infração"
              height="300px"
              width="100%"
              objectFit="contain"
              onClick={onImageModalOpen}
              cursor="pointer"
              bg="black"
            />
          ) : (
            <Flex
              height="300px"
              width="100%"
              bg="gray.100"
              justify="center"
              align="center"
              direction="column"
              gap={2}
            >
              {selectedInfraction.imageLoading ? (
                <>
                  <Spinner size="md" />
                  <Text color="gray.500" fontSize="sm">Carregando imagem...</Text>
                </>
              ) : (
                <Text color="gray.500">Sem imagem disponível</Text>
              )}
            </Flex>
          )}

          {selectedInfraction.plate && (
            <Box position="absolute" top="10px" left="10px">
              <PlateWithSuggestion
                plate={selectedInfraction.plate}
                registeredVehicleType={selectedInfraction.registeredVehicleType}
                suggestion={getActivePlateSuggestion(selectedInfraction)}
                isAccepted={Boolean(acceptedPlateCorrections[selectedInfraction.id])}
                onAccept={() => {
                  const suggestion = getActivePlateSuggestion(selectedInfraction);
                  if (suggestion) {
                    handleAcceptPlateSuggestion(selectedInfraction.id, suggestion);
                  }
                }}
                onReject={() => handleRejectPlateSuggestion(selectedInfraction.id)}
                layout="overlay"
              />
            </Box>
          )}

          <Badge
            position="absolute"
            top="10px"
            right="10px"
            colorScheme={
              selectedInfraction.confidence > 0.9 ? "green" :
                selectedInfraction.confidence > 0.7 ? "yellow" : "orange"
            }
            px={2}
            py={1}
            borderRadius="md"
          >
            Confiança: {(selectedInfraction.confidence * 100).toFixed(1)}%
          </Badge>
        </Box>

        <SimpleGrid columns={2} spacing={4} mb={4}>
          <Box>
            <Text fontSize="xs" color="gray.500">Câmera</Text>
            <Text fontSize="sm">{selectedInfraction.Camera?.name || 'N/A'}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500">Algoritmo</Text>
            <Text fontSize="sm">{selectedInfraction.Algorithm?.name || 'N/A'}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500">Detectado em</Text>
            <Text
              fontSize="sm"
              color={detectedDate.isWeekend ? "red.500" : "inherit"}
              fontWeight={detectedDate.isWeekend ? "bold" : "normal"}
            >
              {detectedDate.text}
            </Text>
          </Box>
          {selectedInfraction.reviewed_at && reviewedDate && (
            <Box>
              <Text fontSize="xs" color="gray.500">Analisado em</Text>
              <Text
                fontSize="sm"
                color={reviewedDate.isWeekend ? "red.500" : "inherit"}
                fontWeight={reviewedDate.isWeekend ? "bold" : "normal"}
              >
                {reviewedDate.text}
              </Text>
            </Box>
          )}
          {selectedInfraction.reviewed_by && (
            <Box>
              <Text fontSize="xs" color="gray.500">Analisado por</Text>
              <Text fontSize="sm">
                {selectedInfraction.reviewer
                  ? (selectedInfraction.reviewer.name || selectedInfraction.reviewer.username || selectedInfraction.reviewer.id)
                  : selectedInfraction.reviewed_by}
              </Text>
            </Box>
          )}
        </SimpleGrid>

        {/* Painel de confirmação com integração de API externa */}
        {showConfirmationPanel && hasApiIntegration && (
          <Box bg="blue.50" p={4} borderRadius="md" mb={4} borderWidth="1px" borderColor="blue.200">
            <Text fontSize="sm" fontWeight="bold" color="blue.700" mb={3}>
              🔗 Confirmação para envio via {apiIntegration?.name || 'API externa'}
            </Text>

            <VStack spacing={3} align="stretch">
              <Box>
                <Text fontSize="xs" fontWeight="medium" mb={1}>Confirmação de Placa</Text>
                <input
                  type="text"
                  value={confirmPlate}
                  onChange={(e) => setConfirmPlate(e.target.value.toUpperCase())}
                  placeholder={selectedInfraction.plate || 'Digite a placa'}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #CBD5E0',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '2px'
                  }}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Corrija a placa caso esteja diferente da que aparece na foto
                </Text>
              </Box>

              <Box>
                <Text fontSize="xs" fontWeight="medium" mb={1}>Códigos de Infração (CTB)</Text>
                <HStack>
                  <input
                    type="text"
                    value={infractionCodeInput}
                    onChange={(e) => setInfractionCodeInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addInfractionCode(); } }}
                    placeholder="Ex: 5185, 7030..."
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #CBD5E0',
                      fontSize: '13px'
                    }}
                  />
                  <Button size="sm" colorScheme="blue" onClick={addInfractionCode}>
                    Adicionar
                  </Button>
                </HStack>
                {selectedInfractionCodes.length > 0 && (
                  <Flex wrap="wrap" gap={1} mt={2}>
                    {selectedInfractionCodes.map((code) => (
                      <Tag key={code} size="sm" colorScheme="purple" borderRadius="full">
                        <TagLabel>{code}</TagLabel>
                        <Box
                          as="button"
                          ml={1}
                          fontSize="xs"
                          onClick={() => removeInfractionCode(code)}
                          cursor="pointer"
                          _hover={{ color: 'red.500' }}
                        >
                          ✕
                        </Box>
                      </Tag>
                    ))}
                  </Flex>
                )}
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Insira os códigos das infrações identificadas (pressione Enter ou clique em Adicionar)
                </Text>
              </Box>

              <HStack spacing={2}>
                <Button
                  colorScheme="green"
                  size="sm"
                  flex={1}
                  onClick={() => reviewInfraction('confirmed', true)}
                  isDisabled={!confirmPlate || submittingBatchReviews}
                >
                  Confirmar e Enviar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowConfirmationPanel(false)}
                >
                  Cancelar
                </Button>
              </HStack>
            </VStack>
          </Box>
        )}

        {/* Indicador de integração ativa */}
        {hasApiIntegration && apiIntegration?.hasRegisterRoute && !showConfirmationPanel && (
          <Box bg="purple.50" px={3} py={2} borderRadius="md" mb={3}>
            <Text fontSize="xs" color="purple.600">
              🔗 Integração ativa: <strong>{apiIntegration?.name}</strong> — As confirmações serão enviadas à API externa ao finalizar o lote.
            </Text>
          </Box>
        )}

        {/* Sempre mostre os botões de confirmação e rejeição, mas desabilite se não puder editar */}
        <HStack spacing={4} justify="center" mt={4}>
          <Button
            colorScheme="green"
            leftIcon={<CheckIcon />}
            isLoading={submittingBatchReviews}
            onClick={() => reviewInfraction('confirmed')}
            flex={1}
            variant={selectedInfraction.status === 'confirmed' ? "solid" : "outline"}
            isDisabled={!canEditBatch || showConfirmationPanel || submittingBatchReviews}
            title={!canEditBatch ? "Você não tem permissão para editar este lote" : ""}
          >
            Confirmar
          </Button>
          <Button
            colorScheme="red"
            leftIcon={<CloseIcon />}
            isLoading={submittingBatchReviews}
            onClick={() => reviewInfraction('rejected')}
            flex={1}
            variant={selectedInfraction.status === 'rejected' ? "solid" : "outline"}
            isDisabled={!canEditBatch || submittingBatchReviews}
            title={!canEditBatch ? "Você não tem permissão para editar este lote" : ""}
          >
            Rejeitar
          </Button>
        </HStack>
      </Box>
    );
  };

  return (
    <Box p={6} bg="gray.100" flex="1" minH="100vh">
      <Flex justify="space-between" align="center" mb={6}>
        <Heading display="flex" alignItems="center">
          <BatchIcon />
          Lotes de Infrações
        </Heading>
        <HStack>
          {userLevel > 3 && (
            <Button
              colorScheme="blue"
              onClick={createBatches}
              isLoading={creatingBatches}
              loadingText="Criando lotes"
            >
              Criar Lotes
            </Button>
          )}
          <Button
            leftIcon={<RefreshIcon />}
            onClick={refreshData}
            variant="outline"
          >
            Atualizar
          </Button>
        </HStack>
      </Flex>

      {/* Estatísticas - apenas para usuários de nível 4 ou superior */}
      {userLevel > 3 && (
        <>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={5} mb={6}>
            <Stat bg="white" p={4} borderRadius="lg" boxShadow="sm">
              <StatLabel>Total de Lotes</StatLabel>
              <StatNumber>{statistics.batches.total}</StatNumber>
              <StatHelpText>Todos os lotes</StatHelpText>
            </Stat>
            <Stat bg="white" p={4} borderRadius="lg" boxShadow="sm">
              <StatLabel>Lotes Disponíveis</StatLabel>
              <StatNumber>{statistics.batches.open}</StatNumber>
              <StatHelpText color="blue.500">
                Aguardando análise
              </StatHelpText>
            </Stat>
            <Stat bg="white" p={4} borderRadius="lg" boxShadow="sm">
              <StatLabel>Lotes Em Análise</StatLabel>
              <StatNumber>{statistics.batches.inProgress}</StatNumber>
              <StatHelpText color="yellow.500">
                Em processamento
              </StatHelpText>
            </Stat>
            <Stat bg="white" p={4} borderRadius="lg" boxShadow="sm">
              <StatLabel>Lotes Concluídos</StatLabel>
              <StatNumber>{statistics.batches.completed}</StatNumber>
              <StatHelpText color="green.500">
                Análise finalizada
              </StatHelpText>
            </Stat>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5} mb={6}>
            <Stat bg="white" p={4} borderRadius="lg" boxShadow="sm">
              <StatLabel>Infrações Pendentes</StatLabel>
              <StatNumber>{statistics.infractions.pending}</StatNumber>
              <StatHelpText color="yellow.500">
                Aguardando análise
              </StatHelpText>
            </Stat>
            <Stat bg="white" p={4} borderRadius="lg" boxShadow="sm">
              <StatLabel>Infrações Confirmadas</StatLabel>
              <StatNumber>{statistics.infractions.confirmed}</StatNumber>
              <StatHelpText color="green.500">
                Validadas positivamente
              </StatHelpText>
            </Stat>
            <Stat bg="white" p={4} borderRadius="lg" boxShadow="sm">
              <StatLabel>Infrações Rejeitadas</StatLabel>
              <StatNumber>{statistics.infractions.rejected}</StatNumber>
              <StatHelpText color="red.500">
                Invalidadas
              </StatHelpText>
            </Stat>
          </SimpleGrid>
        </>
      )}

      {/* Tabs de Lotes */}
      <Tabs variant="soft-rounded" colorScheme="blue" isLazy index={activeTab} onChange={(index) => setActiveTab(index)}>
        <Box position="relative" w="100%" maxW="100%" minW={0} mb={4}>
          <Box
            ref={tabsCarouselRef}
            overflowX="auto"
            pb={1}
            minW={0}
            mx={{ base: 9, md: 0 }}
            onScroll={updateTabsScrollState}
            onPointerDown={handleTabsPointerDown}
            onPointerMove={handleTabsPointerMove}
            onPointerUp={handleTabsPointerUp}
            onPointerLeave={handleTabsPointerUp}
            cursor={{ base: isDraggingTabs ? "grabbing" : "grab", md: "default" }}
            touchAction="pan-x"
            sx={{
              scrollSnapType: { base: "x mandatory", md: "none" },
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none"
            }}
          >
            <TabList minW={{ base: "100%", md: "max-content" }} flexWrap="nowrap" gap={{ base: 2, md: 0 }}>
              {tabsItems.map((tabItem) => (
                <Tab
                  key={tabItem.label}
                  flex={{ base: "0 0 calc((100% - 1rem) / 3)", md: "0 0 auto" }}
                  scrollSnapAlign={{ base: "center", md: "none" }}
                  fontSize={{ base: "xs", md: "sm" }}
                  px={{ base: 2, md: 3 }}
                  py={{ base: 2, md: 2 }}
                  minH={{ base: "56px", md: "auto" }}
                  whiteSpace="normal"
                  textAlign="center"
                  lineHeight="1.2"
                  borderRadius={{ base: "xl", md: "md" }}
                  borderWidth={{ base: "1px", md: "0" }}
                  borderColor={{ base: "gray.200", md: "transparent" }}
                  bg={{ base: "white", md: "transparent" }}
                  _selected={{
                    bg: { base: "blue.500", md: "blue.100" },
                    color: { base: "white", md: "blue.700" },
                    borderColor: { base: "blue.500", md: "transparent" },
                    boxShadow: { base: "sm", md: "none" }
                  }}
                >
                  {tabItem.icon && <Icon as={tabItem.icon} mr={2} />}
                  {tabItem.label}
                </Tab>
              ))}
            </TabList>
          </Box>

          <IconButton
            aria-label="Rolar abas para a esquerda"
            icon={<ChevronLeftIcon boxSize={5} />}
            size="sm"
            position="absolute"
            left="0"
            top="50%"
            transform="translateY(-50%)"
            display={{ base: "inline-flex", md: "none" }}
            borderRadius="full"
            boxShadow="md"
            bg="white"
            _hover={{ bg: "gray.50" }}
            onClick={() => scrollTabsCarousel(-1)}
            isDisabled={!canScrollTabsLeft}
            zIndex={2}
          />

          <IconButton
            aria-label="Rolar abas para a direita"
            icon={<ChevronRightIcon boxSize={5} />}
            size="sm"
            position="absolute"
            right="0"
            top="50%"
            transform="translateY(-50%)"
            display={{ base: "inline-flex", md: "none" }}
            borderRadius="full"
            boxShadow="md"
            bg="white"
            _hover={{ bg: "gray.50" }}
            onClick={() => scrollTabsCarousel(1)}
            isDisabled={!canScrollTabsRight}
            zIndex={2}
          />
        </Box>
        {/* Opções de ordenação */}
        <Flex mb={4} justify="space-between" align="center">
          <Button
            leftIcon={<FilterIcon />}
            size="sm"
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </Button>

          <Flex align="center">
            <Text fontSize="sm" mr={2}>Ordenar por:</Text>
            <Select
              width="auto"
              size="sm"
              value={`${sortOption.field}:${sortOption.direction}`}
              onChange={(e) => {
                const [field, direction] = e.target.value.split(':');
                setSortOption({
                  field,
                  direction,
                  initialized: true
                });
              }}
            >
              <option value="created_at:DESC">Data de criação (mais recente)</option>
              <option value="created_at:ASC">Data de criação (mais antigo)</option>
              <option value="completed_at:DESC">Data de conclusão (mais recente)</option>
              <option value="completed_at:ASC">Data de conclusão (mais antiga)</option>
              <option value="total_infractions:DESC">Quantidade (maior)</option>
              <option value="total_infractions:ASC">Quantidade (menor)</option>
              <option value="id:DESC">ID (maior)</option>
              <option value="id:ASC">ID (menor)</option>
            </Select>
          </Flex>
        </Flex>        {/* Painel de filtros */}
        {showFilters && (
          <Box mb={4} p={4} bg="white" borderRadius="md" boxShadow="sm">
            <Text fontWeight="bold" mb={3}>Filtros</Text>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              <Box>
                <Text fontSize="sm" mb={1}>ID do Lote</Text>
                <input
                  type="text"
                  placeholder="Buscar por ID..."
                  value={pendingFilters.batchId}
                  onChange={(e) => setPendingFilters(prev => ({ ...prev, batchId: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.4rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #E2E8F0'
                  }}
                />
              </Box>
              {userLevel > 3 && (
                <Box>
                  <Text fontSize="sm" mb={1}>Usuário</Text>
                  <UserSearchSelect
                    users={users}
                    value={pendingFilters.assigneeId}
                    onChange={(e) => setPendingFilters(prev => ({ ...prev, assigneeId: e.target.value }))}
                    placeholder={loadingUsers ? "Carregando usuários..." : "Buscar usuário..."}
                    size="sm"
                    isDisabled={loadingUsers}
                  />
                </Box>
              )}

              <Box>
                <Text fontSize="sm" mb={1}>Câmera</Text>
                <CameraSearchSelect
                  cameras={cameras}
                  value={pendingFilters.cameraId}
                  onChange={(e) => setPendingFilters(prev => ({ ...prev, cameraId: e.target.value }))}
                  placeholder={loadingCameras ? "Carregando câmeras..." : "Buscar câmera..."}
                  size="sm"
                  isDisabled={loadingCameras}
                />
              </Box>



              <Box>
                <Text fontSize="sm" mb={1}>Tipo de Infração</Text>
                <Select
                  name="infractionClass"
                  size="sm"
                  placeholder="Todos os tipos"
                  value={pendingFilters.infractionClass}
                  onChange={(e) => setPendingFilters(prev => ({ ...prev, infractionClass: e.target.value }))}
                  bg="white"
                >
                  <option value="">Todos os tipos</option>
                  {INFRACTION_CLASS_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Select>
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>Data Inicial</Text>
                <input
                  type="date"
                  value={pendingFilters.startDate}
                  onChange={(e) => setPendingFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.4rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #E2E8F0'
                  }}
                />
              </Box>
              <Box>
                <Text fontSize="sm" mb={1}>Data Final</Text>
                <input
                  type="date"
                  value={pendingFilters.endDate}
                  onChange={(e) => setPendingFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.4rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #E2E8F0'
                  }}
                />
              </Box>
            </SimpleGrid>
            <Flex justify="flex-end" mt={3}>
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                onClick={clearFilters}
                mr={2}
              >
                Limpar Filtros
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={applyFilters}
              >
                Filtrar
              </Button>
            </Flex>
          </Box>
        )}
        {((userLevel > 3 && filters.assigneeId) || filters.startDate || filters.endDate || filters.cameraId || filters.infractionClass) && (
          <Box mb={4} p={3} bg="blue.50" borderRadius="md" borderLeft="4px" borderColor="blue.500">
            <Flex justify="space-between" align="center">
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="blue.700">
                  Filtros ativos:
                </Text>
                <HStack mt={1} spacing={4} flexWrap="wrap">
                  {userLevel > 3 && filters.assigneeId && (
                    <Badge colorScheme="blue" px={2} py={1} borderRadius="full">
                      Usuário: {users.find(u => u.id === parseInt(filters.assigneeId))?.username || filters.assigneeId}
                    </Badge>
                  )}
                  {filters.cameraId && (
                    <Badge colorScheme="blue" px={2} py={1} borderRadius="full">
                      Câmera: {cameras.find(c => c.id === parseInt(filters.cameraId))?.name || filters.cameraId}
                    </Badge>
                  )}

                  {filters.infractionClass && (
                    <Badge colorScheme="orange" px={2} py={1} borderRadius="full">
                      Tipo: {getInfractionClassLabel(filters.infractionClass)}
                    </Badge>
                  )}
                  {filters.startDate && (
                    <Badge colorScheme="blue" px={2} py={1} borderRadius="full">
                      De: {(() => {
                        const date = new Date(filters.startDate);
                        date.setDate(date.getDate() + 1);
                        return date.toLocaleDateString('pt-BR');
                      })()}
                    </Badge>
                  )}
                  {filters.endDate && (
                    <Badge colorScheme="blue" px={2} py={1} borderRadius="full">
                      Até: {(() => {
                        const date = new Date(filters.endDate);
                        date.setDate(date.getDate() + 1);
                        return date.toLocaleDateString('pt-BR');
                      })()}
                    </Badge>
                  )}
                </HStack>
              </Box>
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                onClick={clearFilters}
              >
                Limpar Filtros
              </Button>
            </Flex>
          </Box>
        )}

        <TabPanels>
          {/* Tab: Meus Lotes */}
          <TabPanel px={0}>
            {batches.user.loading && batches.user.items.length === 0 ? (
              <Flex justify="center" py={10}>
                <Spinner size="xl" />
              </Flex>
            ) : batches.user.items.length === 0 ? (
              <Flex direction="column" align="center" justify="center" py={10} bg="white" borderRadius="md">
                {(filters.assigneeId || filters.startDate || filters.endDate) ? (
                  <>
                    <Text fontSize="xl" mb={4}>Nenhum lote encontrado com os filtros aplicados</Text>
                    <Button
                      colorScheme="blue"
                      onClick={clearFilters}
                    >
                      Limpar Filtros
                    </Button>
                  </>
                ) : (
                  <>
                    <Text fontSize="xl" mb={4}>Você não possui lotes atribuídos</Text>
                    <Button colorScheme="blue" onClick={() => setActiveTab(1)}>
                      Ver Lotes Disponíveis
                    </Button>
                  </>
                )}
              </Flex>
            ) : (
              <>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={5} mb={6}>
                  {batches.user.items.map(batch => (
                    <BatchCard
                      key={batch.id}
                      batch={batch}
                      onAssign={assignBatch}
                      onReview={openBatchDetails}
                      onUnassign={unassignBatch}
                      isUserBatch={true}
                      loadingReviewBatchId={loadingReviewBatchId}
                      loadingUnassignBatchId={loadingUnassignBatchId}
                      userLevel={userLevel}
                      hasBatchInProgress={hasBatchInProgress}
                      canAssignMoreBatches={canAssignMoreBatches}
                      assignRandomBatch={assignRandomBatch}
                    />
                  ))}
                </SimpleGrid>                {batches.user.hasMore && (
                  <Flex justify="center" mt={6}>
                    <Button
                      onClick={() => fetchUserBatches(true)}
                      isLoading={batches.user.loading && batches.user.items.length > 0}
                      loadingText="Carregando"
                    >
                      Carregar Mais
                    </Button>
                  </Flex>
                )}
              </>
            )}
          </TabPanel>

          {/* Tab: Lotes Disponíveis */}
          <TabPanel px={0}>
            {batches.open.loading && batches.open.items.length === 0 ? (
              <Flex justify="center" py={10}>
                <Spinner size="xl" />
              </Flex>
            ) : batches.open.items.length === 0 ? (
              <Flex direction="column" align="center" justify="center" py={10} bg="white" borderRadius="md">
                {(filters.assigneeId || filters.startDate || filters.endDate) ? (
                  <>
                    <Text fontSize="xl" mb={4}>Nenhum lote disponível encontrado com os filtros aplicados</Text>
                    <Button
                      colorScheme="blue"
                      onClick={clearFilters}
                    >
                      Limpar Filtros
                    </Button>
                  </>
                ) : (
                  <>
                    <Text fontSize="xl" mb={4}>Não há lotes disponíveis no momento</Text>
                    <Button colorScheme="blue" onClick={createBatches} isLoading={creatingBatches}>
                      Criar Novos Lotes
                    </Button>
                  </>
                )}
              </Flex>
            ) : (
              <>
                {/* Mensagem informativa para usuários nível 2 e 3 */}
                {userLevel >= 2 && userLevel <= 3 && (
                  <Box mb={4} p={4} bg="blue.50" borderRadius="md" borderLeft="4px" borderColor="blue.500">
                    <Flex align="center" justify="space-between">
                      <Box>
                        <Text fontWeight="bold" fontSize="md" color="blue.700">
                          Lotes em análise: {userActiveBatchesCount} de {MAX_BATCHES_PER_USER}
                        </Text>
                        <Text fontSize="sm" color="blue.600" mt={1}>
                          {canAssignMoreBatches 
                            ? `Você pode escolher qualquer lote abaixo ou usar o botão para pegar um lote aleatório.`
                            : `Você atingiu o limite de ${MAX_BATCHES_PER_USER} lotes simultâneos. Finalize pelo menos um lote antes de pegar outro.`}
                        </Text>
                      </Box>
                      {batches.open.items.length > 0 && canAssignMoreBatches && (
                        <Button
                          colorScheme="blue"
                          size="md"
                          leftIcon={<AssignIcon />}
                          onClick={assignRandomBatch}
                          isLoading={loadingReviewBatchId === 'random'}
                          loadingText="Atribuindo..."
                          ml={4}
                        >
                          Pegar Lote Aleatório
                        </Button>
                      )}
                    </Flex>
                  </Box>
                )}

                {/* Mostrar cards de lotes para todos os usuários */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={5} mb={6}>
                  {batches.open.items.map(batch => (
                    <BatchCard
                      key={batch.id}
                      batch={batch}
                      onAssign={assignBatch}
                      onReview={openBatchDetails}
                      onUnassign={unassignBatch}
                      isUserBatch={false}
                      loadingReviewBatchId={loadingReviewBatchId}
                      loadingUnassignBatchId={loadingUnassignBatchId}
                      userLevel={userLevel}
                      hasBatchInProgress={hasBatchInProgress}
                      canAssignMoreBatches={canAssignMoreBatches}
                      assignRandomBatch={assignRandomBatch}
                    />
                  ))}
                </SimpleGrid>

                {batches.open.hasMore && (
                  <Flex justify="center" mt={6}>
                    <Button
                      onClick={() => fetchBatches('open', true)}
                      isLoading={batches.open.loading && batches.open.items.length > 0}
                      loadingText="Carregando"
                    >
                      Carregar Mais
                    </Button>
                  </Flex>
                )}
              </>
            )}
          </TabPanel>

          {/* Tab: Todos os Lotes */}
          <TabPanel px={0}>
            {batches.all.loading && batches.all.items.length === 0 ? (
              <Flex justify="center" py={10}>
                <Spinner size="xl" />
              </Flex>
            ) : batches.all.items.length === 0 ? (
              <Flex direction="column" align="center" justify="center" py={10} bg="white" borderRadius="md">
                {(filters.assigneeId || filters.startDate || filters.endDate) ? (
                  <>
                    <Text fontSize="xl" mb={4}>Nenhum lote encontrado com os filtros aplicados</Text>
                    <Button
                      colorScheme="blue"
                      onClick={clearFilters}
                    >
                      Limpar Filtros
                    </Button>
                  </>
                ) : (
                  <>
                    <Text fontSize="xl" mb={4}>Não há lotes cadastrados</Text>
                    <Button colorScheme="blue" onClick={createBatches} isLoading={creatingBatches}>
                      Criar Primeiros Lotes
                    </Button>
                  </>
                )}
              </Flex>
            ) : (
              <>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={5} mb={6}>
                  {batches.all.items.map(batch => (
                    <BatchCard
                      key={batch.id}
                      batch={batch}
                      onAssign={assignBatch}
                      onReview={openBatchDetails}
                      onUnassign={unassignBatch}
                      isUserBatch={batch.assignee?.id === parseInt(localStorage.getItem('userId'))}
                      loadingReviewBatchId={loadingReviewBatchId}
                      loadingUnassignBatchId={loadingUnassignBatchId}
                      userLevel={userLevel}
                      canAssignMoreBatches={canAssignMoreBatches}
                    />
                  ))}
                </SimpleGrid>                {batches.all.hasMore && (
                  <Flex justify="center" mt={6}>
                    <Button
                      onClick={() => fetchBatches('all', true)}
                      isLoading={batches.all.loading && batches.all.items.length > 0}
                      loadingText="Carregando"
                    >
                      Carregar Mais
                    </Button>
                  </Flex>
                )}
              </>
            )}
          </TabPanel>

          {/* Tab: Em Análise */}
          <TabPanel px={0}>
            {batches.inProgress.loading ? (
              <Flex justify="center" py={10}>
                <Spinner size="xl" />
              </Flex>
            ) : batches.inProgress.items.length === 0 ? (
              <Flex direction="column" align="center" justify="center" py={10} bg="white" borderRadius="md">
                {(filters.assigneeId || filters.startDate || filters.endDate) ? (
                  <>
                    <Text fontSize="xl" mb={4}>Nenhum lote em análise encontrado com os filtros aplicados</Text>
                    <Button
                      colorScheme="blue"
                      onClick={clearFilters}
                    >
                      Limpar Filtros
                    </Button>
                  </>
                ) : (
                  <Text fontSize="xl">Não há lotes em análise no momento</Text>
                )}
              </Flex>
            ) : (
              <>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={5} mb={6}>
                  {batches.inProgress.items.map(batch => (
                    <BatchCard
                      key={batch.id}
                      batch={batch}
                      onAssign={assignBatch}
                      onReview={openBatchDetails}
                      onUnassign={unassignBatch}
                      isUserBatch={batch.assignee?.id === parseInt(localStorage.getItem('userId'))}
                      loadingReviewBatchId={loadingReviewBatchId}
                      loadingUnassignBatchId={loadingUnassignBatchId}
                      userLevel={userLevel}
                      canAssignMoreBatches={canAssignMoreBatches}
                    />
                  ))}
                </SimpleGrid>                {batches.inProgress.hasMore && (
                  <Flex justify="center" mt={6}>
                    <Button
                      onClick={() => fetchBatches('in_progress', true)}
                      isLoading={batches.inProgress.loading && batches.inProgress.items.length > 0}
                      loadingText="Carregando"
                    >
                      Carregar Mais
                    </Button>
                  </Flex>
                )}
              </>
            )}
          </TabPanel>

          {/* Tab: Concluídos */}
          <TabPanel px={0}>
            {!batches.completed.items.length && batches.completed.loading ? (
              <Flex justify="center" py={10}>
                <Spinner size="xl" />
              </Flex>
            ) : batches.completed.items.length === 0 ? (
              <Flex direction="column" align="center" justify="center" py={10} bg="white" borderRadius="md">
                {(filters.assigneeId || filters.startDate || filters.endDate) ? (
                  <>
                    <Text fontSize="xl" mb={4}>Nenhum lote concluído encontrado com os filtros aplicados</Text>
                    <Button
                      colorScheme="blue"
                      onClick={clearFilters}
                    >
                      Limpar Filtros
                    </Button>
                  </>
                ) : (
                  <Text fontSize="xl">Não há lotes concluídos</Text>
                )}
              </Flex>
            ) : (
              <>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={5} mb={6}>
                  {batches.completed.items.map(batch => (
                    <BatchCard
                      key={batch.id}
                      batch={batch}
                      onReview={openBatchDetails}
                      onUnassign={unassignBatch}
                      isUserBatch={batch.assignee?.id === parseInt(localStorage.getItem('userId'))}
                      loadingReviewBatchId={loadingReviewBatchId}
                      loadingUnassignBatchId={loadingUnassignBatchId}
                      userLevel={userLevel}
                      canAssignMoreBatches={canAssignMoreBatches}
                    />
                  ))}
                </SimpleGrid>                {batches.completed.hasMore && (
                  <Flex justify="center" mt={6}>
                    <Button
                      onClick={() => fetchBatches('completed', true)}
                      isLoading={batches.completed.loading && batches.completed.items.length > 0}
                      loadingText="Carregando"
                    >
                      Carregar Mais
                    </Button>
                  </Flex>
                )}
              </>
            )}
          </TabPanel>

          {/* Tab: Analítico - Apenas para usuários nível 4 e 5 */}
          {userLevel >= 4 && (
            <TabPanel px={0}>
              {loadingAnalytics ? (
                <Flex justify="center" py={10}>
                  <Spinner size="xl" />
                </Flex>
              ) : analyticsData ? (
                <VStack align="stretch" spacing={6}>
                  {/* Seção 1: Estatísticas por Período */}
                  <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
                    <Heading size="md" mb={4} display="flex" alignItems="center">
                      <CalendarIcon mr={2} />
                      Estatísticas por Período
                    </Heading>

                    {/* Botões de período fixo */}
                    <HStack spacing={2} mb={4} flexWrap="wrap">
                      {[
                        { key: 'today', label: 'Hoje' },
                        { key: 'yesterday', label: 'Ontem' },
                        { key: 'this_week', label: 'Esta Semana' },
                        { key: 'this_month', label: 'Este Mês' }
                      ].map(p => (
                        <Button
                          key={p.key}
                          size="sm"
                          colorScheme={selectedPeriod === p.key ? 'blue' : 'gray'}
                          variant={selectedPeriod === p.key ? 'solid' : 'outline'}
                          onClick={() => handleSelectPeriod(p.key)}
                        >
                          {p.label}
                        </Button>
                      ))}
                    </HStack>

                    {/* Período personalizado */}
                    <Box mb={5} p={4} bg="gray.50" borderRadius="md" borderLeft="4px" borderColor="purple.400">
                      <Text fontSize="sm" fontWeight="bold" mb={2} color="purple.700">Período Personalizado</Text>
                      <Flex gap={3} flexWrap="wrap" align="flex-end">
                        <Box>
                          <Text fontSize="xs" mb={1} color="gray.600">Data Início</Text>
                          <input
                            type="date"
                            value={customPeriodStart}
                            onChange={e => setCustomPeriodStart(e.target.value)}
                            style={{
                              padding: '0.4rem 0.6rem',
                              borderRadius: '0.375rem',
                              border: '1px solid #CBD5E0',
                              fontSize: '0.875rem',
                              background: 'white'
                            }}
                          />
                        </Box>
                        <Box>
                          <Text fontSize="xs" mb={1} color="gray.600">Data Fim</Text>
                          <input
                            type="date"
                            value={customPeriodEnd}
                            onChange={e => setCustomPeriodEnd(e.target.value)}
                            style={{
                              padding: '0.4rem 0.6rem',
                              borderRadius: '0.375rem',
                              border: '1px solid #CBD5E0',
                              fontSize: '0.875rem',
                              background: 'white'
                            }}
                          />
                        </Box>
                        <Button
                          size="sm"
                          colorScheme="purple"
                          onClick={handleApplyCustomPeriod}
                          isDisabled={!customPeriodStart || !customPeriodEnd}
                          leftIcon={<FilterIcon />}
                        >
                          Aplicar
                        </Button>
                      </Flex>
                    </Box>

                    {/* Resultados */}
                    {loadingPeriodStats ? (
                      <Flex justify="center" py={6}>
                        <Spinner size="lg" color="blue.500" />
                      </Flex>
                    ) : periodStats ? (
                      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                        <Stat bg="blue.50" p={4} borderRadius="md" borderLeft="4px" borderColor="blue.500">
                          <StatLabel>Gerados</StatLabel>
                          <StatNumber color="blue.600">{periodStats.generated ?? 0}</StatNumber>
                          <StatHelpText>Lotes criados no período</StatHelpText>
                        </Stat>
                        <Stat bg="green.50" p={4} borderRadius="md" borderLeft="4px" borderColor="green.500">
                          <StatLabel>Concluídos</StatLabel>
                          <StatNumber color="green.600">{periodStats.completed ?? 0}</StatNumber>
                          <StatHelpText>Lotes finalizados no período</StatHelpText>
                        </Stat>
                        <Stat bg="yellow.50" p={4} borderRadius="md" borderLeft="4px" borderColor="yellow.500">
                          <StatLabel>Em Análise</StatLabel>
                          <StatNumber color="yellow.700">{periodStats.inProgress ?? 0}</StatNumber>
                          <StatHelpText>Criados e ainda em análise</StatHelpText>
                        </Stat>
                        <Stat bg="gray.50" p={4} borderRadius="md" borderLeft="4px" borderColor="gray.400">
                          <StatLabel>Disponíveis</StatLabel>
                          <StatNumber color="gray.700">{periodStats.open ?? 0}</StatNumber>
                          <StatHelpText>Criados e sem analista</StatHelpText>
                        </Stat>
                      </SimpleGrid>
                    ) : (
                      <Text color="gray.500" fontSize="sm">Selecione um período para ver as estatísticas.</Text>
                    )}
                  </Box>

                  {/* Seção 3: Estatísticas Temporais Gerais */}
                  <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
                    <Flex justify="space-between" align="center" mb={4}>
                      <Heading size="md" display="flex" alignItems="center">
                        <CalendarIcon mr={2} />
                        Estatísticas Temporais
                      </Heading>
                      <HStack spacing={3}>
                        {analyticsLoadedAt && (
                          <Text fontSize="xs" color="gray.400">
                            Carregado às {analyticsLoadedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </Text>
                        )}
                        <Button
                          size="xs"
                          leftIcon={<RefreshIcon />}
                          onClick={fetchAnalyticsData}
                          isLoading={loadingAnalytics}
                          colorScheme="blue"
                          variant="outline"
                        >
                          Atualizar
                        </Button>
                      </HStack>
                    </Flex>
                    <Text fontSize="xs" color="gray.500" mb={4}>
                      Janela móvel de 24h e acumulados desde o início da semana/mês. Use "Estatísticas por Período" acima para consultas precisas por dia.
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                      <Stat bg="blue.50" p={4} borderRadius="md" borderLeft="4px" borderColor="blue.500">
                        <StatLabel>Últimas 24h (janela móvel)</StatLabel>
                        <StatNumber>{analyticsData.batchesLast24h || 0}</StatNumber>
                        <StatHelpText>De 24h atrás até agora</StatHelpText>
                      </Stat>
                      <Stat bg="green.50" p={4} borderRadius="md" borderLeft="4px" borderColor="green.500">
                        <StatLabel>Esta Semana (acumulado)</StatLabel>
                        <StatNumber>{analyticsData.batchesThisWeek || 0}</StatNumber>
                        <StatHelpText>Desde segunda-feira</StatHelpText>
                      </Stat>
                      <Stat bg="purple.50" p={4} borderRadius="md" borderLeft="4px" borderColor="purple.500">
                        <StatLabel>Este Mês (acumulado)</StatLabel>
                        <StatNumber>{analyticsData.batchesThisMonth || 0}</StatNumber>
                        <StatHelpText>Desde o dia 1º</StatHelpText>
                      </Stat>
                    </SimpleGrid>
                  </Box>

                  {/* Seção 3: Câmeras mais Produtivas */}
                  <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
                    <Heading size="md" mb={4} display="flex" alignItems="center">
                      <ViewIcon mr={2} />
                      Top Câmeras - Mais Lotes Gerados
                    </Heading>
                    {processedAnalyticsData.topCameras && processedAnalyticsData.topCameras.length > 0 ? (
                      <VStack align="stretch" spacing={3}>
                        {processedAnalyticsData.topCameras.map((camera, index) => (
                          <Box key={camera.camera_id} p={4} borderRadius="md" bg="gray.50" borderLeft="4px" borderColor="blue.500">
                            <Flex justify="space-between" align="center" mb={2}>
                              <HStack spacing={3}>
                                <Badge colorScheme="blue" borderRadius="full" px={2} py={1}>
                                  #{index + 1}
                                </Badge>
                                <Text fontWeight="bold">{camera.camera_name}</Text>
                              </HStack>
                              <Badge colorScheme="green" fontSize="lg" px={3} py={1}>
                                {camera.batch_count} lotes
                              </Badge>
                            </Flex>
                            <Progress
                              value={(camera.batch_count / (processedAnalyticsData.topCameras[0]?.batch_count || 1)) * 100}
                              colorScheme="blue"
                              borderRadius="md"
                              size="sm"
                            />
                          </Box>
                        ))}
                      </VStack>
                    ) : (
                      <Text>Nenhum dado disponível</Text>
                    )}
                  </Box>

                  {/* Seção 4: Distribuição por Dia */}
                  {analyticsData.batchesByDay && analyticsData.batchesByDay.length > 0 && (
                    <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
                      <Heading size="md" mb={4} display="flex" alignItems="center">
                        <CalendarIcon mr={2} />
                        Distribuição de Lotes por Dia
                      </Heading>
                      <VStack align="stretch" spacing={2}>
                        {analyticsData.batchesByDay.map(day => (
                          <Box key={day.date} p={3} borderRadius="md" bg="gray.50">
                            <Flex justify="space-between" align="center" mb={1}>
                              <Text fontWeight="bold">{new Date(day.date).toLocaleDateString('pt-BR')}</Text>
                              <Badge colorScheme="purple">{day.batch_count} lotes</Badge>
                            </Flex>
                            <Progress
                              value={(day.batch_count / (analyticsData.batchesByDay[0]?.batch_count || 1)) * 100}
                              colorScheme="purple"
                              borderRadius="md"
                              size="sm"
                            />
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  )}

                  {/* Opções de Visualização */}
                  <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
                    <Heading size="sm" mb={4}>Opções de Visualização</Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4}>
                      <Box>
                        <Text fontSize="sm" mb={2} fontWeight="bold">Status dos Lotes</Text>
                        <Select
                          value={analyticsFilters.batchStatus}
                          onChange={(e) => setAnalyticsFilters(prev => ({ ...prev, batchStatus: e.target.value }))}
                          size="sm"
                        >
                          <option value="all">Todos</option>
                          <option value="open">Disponíveis</option>
                          <option value="in_progress">Em Análise</option>
                          <option value="completed">Concluídos</option>
                        </Select>
                      </Box>
                      <Box>
                        <Text fontSize="sm" mb={2} fontWeight="bold">Ordenar por</Text>
                        <Select
                          value={analyticsFilters.sortBy}
                          onChange={(e) => setAnalyticsFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                          size="sm"
                        >
                          <option value="name">Nome da Câmera</option>
                          <option value="count">Número de Lotes</option>
                        </Select>
                      </Box>
                      <Box>
                        <Text fontSize="sm" mb={2} fontWeight="bold">Ordem</Text>
                        <Select
                          value={analyticsFilters.sortOrder}
                          onChange={(e) => setAnalyticsFilters(prev => ({ ...prev, sortOrder: e.target.value }))}
                          size="sm"
                        >
                          <option value="ASC">Crescente</option>
                          <option value="DESC">Decrescente</option>
                        </Select>
                      </Box>
                      <Box>
                        <Text fontSize="sm" mb={2} fontWeight="bold">Filtro de Limiar</Text>
                        <Flex gap={2}>
                          <input
                            type="number"
                            min={0}
                            value={analyticsFilters.thresholdValue}
                            onChange={(e) => setAnalyticsFilters(prev => ({ ...prev, thresholdValue: parseInt(e.target.value) || 0 }))}
                            style={{
                              flex: 1,
                              padding: '0.4rem',
                              borderRadius: '0.375rem',
                              border: '1px solid #E2E8F0',
                              fontSize: '0.875rem'
                            }}
                            placeholder="Mínimo de lotes"
                          />
                        </Flex>
                      </Box>
                      <Box>
                        <Text fontSize="sm" mb={2} fontWeight="bold">Mostrar Acima do Limiar</Text>
                        <HStack>
                          <input
                            type="checkbox"
                            checked={analyticsFilters.showOnlyAboveThreshold}
                            onChange={(e) => setAnalyticsFilters(prev => ({ ...prev, showOnlyAboveThreshold: e.target.checked }))}
                            style={{
                              width: '1.2rem',
                              height: '1.2rem',
                              cursor: 'pointer'
                            }}
                          />
                          <Text fontSize="sm">Ativar</Text>
                        </HStack>
                      </Box>
                    </SimpleGrid>
                  </Box>

                  {/* Seção 5: Lotes por Câmera */}
                  <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
                    <Heading size="md" mb={4} display="flex" alignItems="center">
                      <CameraIcon mr={2} />
                      Lotes por Câmera
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                      {processedAnalyticsData.batchesByCamera && processedAnalyticsData.batchesByCamera.length > 0 ? (
                        processedAnalyticsData.batchesByCamera.map(camera => (
                          <Card key={camera.camera_id} variant="outline">
                            <CardBody>
                              <VStack align="stretch" spacing={2}>
                                <Text fontWeight="bold" fontSize="lg">
                                  {camera.camera_name}
                                </Text>
                                <HStack justify="space-between">
                                  <Text color="gray.600">Total de Lotes:</Text>
                                  <Badge colorScheme="blue" fontSize="lg" px={3} py={1}>
                                    {camera.batch_count}
                                  </Badge>
                                </HStack>
                              </VStack>
                            </CardBody>
                          </Card>
                        ))
                      ) : (
                        <Text>Nenhum dado disponível</Text>
                      )}
                    </SimpleGrid>
                  </Box>
                </VStack>
              ) : (
                <Flex direction="column" align="center" justify="center" py={10} bg="white" borderRadius="md">
                  <Text fontSize="xl" mb={4}>Nenhum dado analítico disponível</Text>
                </Flex>
              )}
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>

      {/* Modal de carregamento do lote */}
      <Modal isOpen={isLoadingBatchModal} onClose={() => {}} closeOnOverlayClick={false} closeOnEsc={false} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalBody py={10} textAlign="center">
            <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
            <Heading size="md" mb={2}>Carregando lote</Heading>
            <Text color="gray.600">{batchLoadingMessage}</Text>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal para análise de infrações de um lote */}
      <Modal isOpen={isBatchModalOpen} onClose={handleCloseBatchModal} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW="900px">
          <ModalHeader>
            <Flex justify="space-between" align="center">
              <Text>Análise do Lote: {selectedBatch?.name}</Text>
              <Badge colorScheme={getBatchStatusColor(selectedBatch?.status)}>
                {getBatchStatusText(selectedBatch?.status)}
              </Badge>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pb={6}>
            {loadingInfractions ? (
              <Flex justify="center" py={10}>
                <Spinner size="xl" />
              </Flex>
            ) : (
              <>
                <HStack mb={4} justify="space-between">
                  <HStack>
                    <Text fontSize="sm">Câmera:</Text>
                    <Badge colorScheme="green" fontSize="sm">
                      {selectedBatch?.Camera?.name || 'N/A'}
                    </Badge>
                    {selectedBatch?.Camera?.latitude && selectedBatch?.Camera?.longitude && (
                      <IconButton
                        icon={<ExternalLinkIcon />}
                        size="xs"
                        colorScheme="blue"
                        variant="ghost"
                        aria-label="Abrir no Google Maps"
                        title="Abrir no Google Maps"
                        onClick={() => window.open(`https://www.google.com/maps?q=${selectedBatch.Camera.latitude},${selectedBatch.Camera.longitude}`, '_blank')}
                      />
                    )}
                  </HStack>

                </HStack>

                <Box bg="gray.100" p={3} borderRadius="md" mb={4}>
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontSize="sm">Progresso:</Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {selectedBatch?.analyzed_infractions} de {selectedBatch?.total_infractions} infrações analisadas
                    </Text>
                  </Flex>
                  <Progress
                    value={(selectedBatch?.analyzed_infractions / selectedBatch?.total_infractions) * 100}
                    colorScheme={
                      selectedBatch?.status === 'completed' ? 'green' :
                        selectedBatch?.status === 'in_progress' ? 'yellow' : 'blue'
                    }
                    size="sm"
                    borderRadius="md"
                    mb={2}
                  />
                  <Flex justify="space-between" fontSize="xs">
                    <Text>Infração {currentInfractionIndex + 1} de {infractions.length}</Text>
                    <HStack spacing={4}>
                      <Text>{infractions.filter(inf => inf.status === 'pending').length} pendentes</Text>
                      <Text color="green.500">{infractions.filter(inf => inf.status === 'confirmed').length} confirmadas</Text>
                      <Text color="red.500">{infractions.filter(inf => inf.status === 'rejected').length} rejeitadas</Text>
                    </HStack>
                  </Flex>
                </Box>

                <Divider mb={4} />

                {/* Navegação entre infrações */}
                <Flex justify="space-between" mb={4}>
                  <Button
                    leftIcon={<ChevronLeftIcon />}
                    onClick={goToPreviousInfraction}
                    isDisabled={currentInfractionIndex === 0}
                    size="sm"
                  >
                    Anterior
                  </Button>

                  <HStack>
                    <Button
                      colorScheme="blue"
                      variant="outline"
                      size="sm"
                      onClick={() => goToNextPendingInfraction()}
                    >
                      Próxima pendente
                    </Button>
                  </HStack>

                  <Button
                    rightIcon={<ChevronRightIcon />}
                    onClick={goToNextInfraction}
                    isDisabled={currentInfractionIndex === infractions.length - 1}
                    size="sm"
                  >
                    Próxima
                  </Button>
                </Flex>

                {/* Detalhes da infração atual */}
                {renderInfractionDetails()}
              </>
            )}
          </ModalBody>

          <ModalFooter>
            {/* Adicionar botão "Finalizar Lote" apenas quando for a última infração e o lote estiver em análise */}
            {selectedBatch?.status === 'in_progress' &&
              currentInfractionIndex === infractions.length - 1 &&
              !infractions.some(inf => inf.status === 'pending') && (
                <Button
                  colorScheme="green"
                  mr={3}
                  onClick={completeBatch}
                >
                  Finalizar Lote
                </Button>
              )}
            <Button onClick={handleCloseBatchModal}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal para exibir imagem em tamanho maior */}
      <Modal isOpen={isImageModalOpen} onClose={handleCloseImageReviewModal} size="full">
        <ModalOverlay />
        <ModalContent>
          <ModalBody
            display="flex"
            height="100vh"
            position="relative"
            p={0}
          >
            {selectedInfraction && (
              <>
                {/* Painel lateral esquerdo - Controles de ação */}
                <VStack
                  width="250px"
                  bg="gray.50"
                  px={6}
                  py={3}
                  justifyContent="center"
                  spacing={6}
                  borderRight="1px solid"
                  borderColor="gray.200"
                >
                  <VStack spacing={4} width="100%">
                    <Text fontSize="lg" fontWeight="bold" color="gray.700">
                      Ações
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      C Confirmar • R Rejeitar • P Pendente • ← / → Navegar • X Fechar
                    </Text>
                    {hasUnsavedReviews && (
                      <Badge colorScheme="orange" width="100%" justifyContent="center" py={1}>
                        Salvando validações...
                      </Badge>
                    )}
                    {backgroundImagesLoading && (
                      <Text fontSize="xs" color="gray.500" textAlign="center">
                        Carregando fotos: {backgroundImagesLoaded}/{backgroundImagesTotal}
                      </Text>
                    )}

                    <Button
                      colorScheme="green"
                      leftIcon={<CheckIcon />}
                      isLoading={submittingBatchReviews}
                      isDisabled={!canEditBatch || submittingBatchReviews}
                      onClick={() => reviewInfraction('confirmed')}
                      width="100%"
                      size="lg"
                      variant={selectedInfraction.status === 'confirmed' ? "solid" : "outline"}
                    >
                      Confirmar
                    </Button>

                    <Button
                      colorScheme="red"
                      leftIcon={<CloseIcon />}
                      isLoading={submittingBatchReviews}
                      isDisabled={!canEditBatch || submittingBatchReviews}
                      onClick={() => reviewInfraction('rejected')}
                      width="100%"
                      size="lg"
                      variant={selectedInfraction.status === 'rejected' ? "solid" : "outline"}
                    >
                      Rejeitar
                    </Button>
                  </VStack>

                  <Divider />

                  <VStack spacing={4} width="100%">
                    <Text fontSize="lg" fontWeight="bold" color="gray.700">
                      Contadores
                    </Text>

                    <VStack width="100%" spacing={2}>
                      <HStack width="100%" justify="space-between">
                        <Text fontSize="sm" color="gray.600">
                          Confirmadas
                        </Text>
                        <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                          {infractions.filter(inf => inf.status === 'confirmed').length}
                        </Badge>
                      </HStack>

                      <HStack width="100%" justify="space-between">
                        <Text fontSize="sm" color="gray.600">
                          Rejeitadas
                        </Text>
                        <Badge colorScheme="red" fontSize="md" px={3} py={1}>
                          {infractions.filter(inf => inf.status === 'rejected').length}
                        </Badge>
                      </HStack>

                      <HStack width="100%" justify="space-between">
                        <Text fontSize="sm" color="gray.600">
                          Pendentes
                        </Text>
                        <Badge colorScheme="yellow" fontSize="md" px={3} py={1}>
                          {infractions.filter(inf => inf.status === 'pending').length}
                        </Badge>
                      </HStack>
                    </VStack>
                  </VStack>

                  <Divider />

                  <VStack spacing={4} width="100%">
                    <Text fontSize="lg" fontWeight="bold" color="gray.700">
                      Navegação
                    </Text>

                    <HStack width="100%" spacing={2}>
                      <Button
                        leftIcon={<ChevronLeftIcon />}
                        onClick={goToPreviousInfraction}
                        isDisabled={currentInfractionIndex === 0}
                        flex={1}
                        variant="outline"
                        size="sm"
                      >
                        Anterior
                      </Button>

                      <Button
                        rightIcon={<ChevronRightIcon />}
                        onClick={goToNextInfraction}
                        isDisabled={currentInfractionIndex === infractions.length - 1}
                        flex={1}
                        variant="outline"
                        size="sm"
                      >
                        Próxima
                      </Button>
                    </HStack>

                    <Text fontSize="sm" color="gray.600" textAlign="center">
                      {currentInfractionIndex + 1} de {infractions.length}
                    </Text>

                    <Button
                      colorScheme="blue"
                      variant="outline"
                      width="100%"
                      onClick={() => goToNextPendingInfraction()}
                      isDisabled={!infractions.some(inf => inf.status === 'pending')}
                      size="md"
                    >
                      Próxima Pendente
                    </Button>

                    {canEditBatch
                      && selectedBatch?.status === 'in_progress'
                      && !infractions.some((inf) => inf.status === 'pending') && (
                      <Button
                        colorScheme="green"
                        width="100%"
                        onClick={completeBatch}
                        isLoading={submittingBatchReviews}
                        size="md"
                      >
                        Finalizar Lote
                      </Button>
                    )}
                  </VStack>
                </VStack>

                {/* Área central da imagem */}
                <Box
                  flex="1"
                  position="relative"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  overflow="hidden"
                  onMouseDown={selectedInfraction.imageUrl ? handleMouseDown : undefined}
                  onWheel={selectedInfraction.imageUrl ? handleWheel : undefined}
                  cursor={selectedInfraction.imageUrl && zoomLevel > 1 ? 'grab' : 'default'}
                  style={{ touchAction: "none" }}
                >
                  {!selectedInfraction.imageUrl ? (
                    <VStack spacing={3}>
                      <Spinner size="xl" color="blue.400" />
                      <Text color="gray.600">
                        {selectedInfraction.imageLoading ? 'Carregando foto...' : 'Sem imagem disponível'}
                      </Text>
                    </VStack>
                  ) : (
                  <>
                  <Box
                    ref={imageRef}
                    position="relative"
                    height="100%"
                    width="100%"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    style={{
                      transform: `scale(${zoomLevel}) translate(${position.x / zoomLevel}px, ${position.y / zoomLevel}px)`,
                      transformOrigin: "center",
                      transition: isDragging ? "none" : "transform 0.2s ease-out"
                    }}
                  >
                    <Image
                      src={selectedInfraction.imageUrl}
                      alt="Imagem da infração"
                      maxH="90vh"
                      maxW="100%"
                      objectFit="contain"
                      draggable="false"
                      onLoad={(e) => {
                        setImageNaturalWidth(e.target.naturalWidth || 1);
                        setImageNaturalHeight(e.target.naturalHeight || 1);
                      }}
                    />
                    {/* Overlay bbox */}
                    {showBbox && selectedInfraction?.bbox && (() => {
                      const parts = selectedInfraction.bbox.split(',').map(Number);
                      if (parts.length !== 4 || parts.some(isNaN)) return null;
                      const [x1, y1, x2, y2] = parts;
                      return (
                        <Box
                          key="bbox-overlay"
                          position="absolute"
                          border="3px solid red"
                          borderRadius="2px"
                          pointerEvents="none"
                          style={{
                            left: `${(x1 / imageNaturalWidth) * 100}%`,
                            top: `${(y1 / imageNaturalHeight) * 100}%`,
                            width: `${((x2 - x1) / imageNaturalWidth) * 100}%`,
                            height: `${((y2 - y1) / imageNaturalHeight) * 100}%`,
                          }}
                        />
                      );
                    })()}
                  </Box>

                  {/* Controles de zoom */}
                  <Flex
                    position="absolute"
                    bottom="20px"
                    right="20px"
                    bg="blackAlpha.700"
                    borderRadius="md"
                    p={2}
                    direction="column"
                    align="center"
                    color="white"
                    zIndex={1000}
                  >
                    <IconButton
                      icon={<ChevronUpIcon />}
                      onClick={zoomIn}
                      aria-label="Zoom in"
                      variant="ghost"
                      color="white"
                      size="sm"
                      _hover={{ bg: "blackAlpha.400" }}
                    />
                    <Text fontWeight="bold" fontSize="sm" my={1}>
                      {Math.round(zoomLevel * 100)}%
                    </Text>
                    <IconButton
                      icon={<ChevronDownIcon />}
                      onClick={zoomOut}
                      aria-label="Zoom out"
                      variant="ghost"
                      color="white"
                      size="sm"
                      _hover={{ bg: "blackAlpha.400" }}
                    />
                    <Divider my={1} />
                    <IconButton
                      icon={<RepeatIcon />}
                      onClick={resetZoom}
                      aria-label="Reset zoom"
                      variant="ghost"
                      color="white"
                      size="sm"
                      _hover={{ bg: "blackAlpha.400" }}
                    />
                  </Flex>

                  {/* Botão toggle bbox */}
                  <Button
                    position="absolute"
                    bottom="20px"
                    left="50%"
                    transform="translateX(-50%)"
                    size="sm"
                    colorScheme={showBbox ? 'red' : 'blue'}
                    onClick={() => setShowBbox(v => !v)}
                    isDisabled={!selectedInfraction?.bbox}
                    opacity={selectedInfraction?.bbox ? 0.9 : 0.3}
                    zIndex={1000}
                  >
                    {showBbox ? 'Ocultar Detecção' : 'Visualizar Detecção'}
                  </Button>
                  </>
                  )}
                </Box>

                {/* Painel lateral direito - Informações da infração */}
                <VStack
                  width="300px"
                  bg="gray.50"
                  px={6}
                  py={3}
                  spacing={4}
                  borderLeft="1px solid"
                  borderColor="gray.200"
                  alignItems="stretch"
                >
                  <VStack spacing={4} alignItems="stretch">
                    <HStack justify="space-between" align="center">
                      <Text fontSize="lg" fontWeight="bold" color="gray.700">
                        Informações da Infração
                      </Text>
                      <IconButton
                        aria-label="Fechar modal"
                        icon={<CloseIcon boxSize={3} />}
                        size="sm"
                        variant="ghost"
                        onClick={handleCloseImageReviewModal}
                      />
                    </HStack>

                    <Box bg="white" p={3} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                      <Flex justify="space-between" align="center" mb={2}>
                        <Text fontSize="sm" fontWeight="bold">Progresso do Lote</Text>
                        <Text fontSize="xs" color="gray.600">
                          {selectedBatch?.analyzed_infractions || 0} de {selectedBatch?.total_infractions || 0}
                        </Text>
                      </Flex>
                      <Progress
                        value={selectedBatch?.total_infractions ? ((selectedBatch?.analyzed_infractions || 0) / selectedBatch.total_infractions) * 100 : 0}
                        colorScheme={
                          selectedBatch?.status === 'completed' ? 'green' :
                            selectedBatch?.status === 'in_progress' ? 'yellow' : 'blue'
                        }
                        size="sm"
                        borderRadius="md"
                        mb={2}
                      />
                      <HStack spacing={3} justify="space-between" fontSize="xs" color="gray.700">
                        <Text color="green.600">Confirmadas: {infractions.filter(inf => inf.status === 'confirmed').length}</Text>
                        <Text color="red.600">Rejeitadas: {infractions.filter(inf => inf.status === 'rejected').length}</Text>
                        <Text color="yellow.700">Pendentes: {infractions.filter(inf => inf.status === 'pending').length}</Text>
                      </HStack>
                    </Box>

                    <Box>
                      <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
                        Data e Hora
                      </Text>
                      <Text
                        fontSize="md"
                        fontWeight="semibold"
                        color={(() => {
                          const detectedDate = formatDateWithWeekday(selectedInfraction.detected_at);
                          return detectedDate.isWeekend ? "red.500" : "inherit";
                        })()}
                      >
                        {(() => {
                          const detectedDate = formatDateWithWeekday(selectedInfraction.detected_at);
                          const [dayAndDate] = detectedDate.text.split(' - ');
                          return dayAndDate;
                        })()}
                      </Text>
                      <Text fontSize="md" color="gray.600">
                        {new Date(selectedInfraction.detected_at).toLocaleTimeString('pt-BR', {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </Text>
                    </Box>

                    <Divider />

                    <Box>
                      <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
                        Câmera
                      </Text>
                      <Text fontSize="md" fontWeight="semibold">
                        {selectedInfraction.Camera?.name || 'N/A'}
                      </Text>
                    </Box>

                    <Divider />

                    <Box>
                      <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
                        Detecção
                      </Text>
                      <Text fontSize="md" fontWeight="semibold">
                        {getInfractionClassLabel(selectedInfraction) || selectedInfraction.Algorithm?.name || 'N/A'}
                      </Text>
                      <Text fontSize="xs" color="gray.500" mt={0.5}>
                        {selectedInfraction.Algorithm?.name || ''}
                      </Text>
                    </Box>

                    <Divider />

                    <HStack spacing={3} align="flex-start">
                      <Box flex={1}>
                        <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
                          Confiança
                        </Text>
                        <Badge
                          colorScheme={
                            selectedInfraction.confidence > 0.9 ? "green" :
                              selectedInfraction.confidence > 0.7 ? "yellow" : "orange"
                          }
                          px={2}
                          py={1}
                          borderRadius="md"
                        >
                          {(selectedInfraction.confidence * 100).toFixed(1)}%
                        </Badge>
                      </Box>

                      <Box flex={1}>
                        <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
                          Status
                        </Text>
                        <Badge
                          colorScheme={
                            selectedInfraction.status === 'confirmed' ? 'green' :
                              selectedInfraction.status === 'rejected' ? 'red' : 'yellow'
                          }
                          px={2}
                          py={1}
                          borderRadius="md"
                        >
                          {selectedInfraction.status === 'confirmed' ? 'Confirmada' :
                            selectedInfraction.status === 'rejected' ? 'Rejeitada' : 'Pendente'}
                        </Badge>
                      </Box>
                    </HStack>

                    {selectedInfraction.reviewed_at && (
                      <>
                        <Divider />
                        <Box>
                          <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
                            Analisado em
                          </Text>
                          <Text
                            fontSize="sm"
                            color={(() => {
                              const reviewedDate = formatDateWithWeekday(selectedInfraction.reviewed_at);
                              return reviewedDate.isWeekend ? "red.500" : "gray.600";
                            })()}
                            fontWeight={(() => {
                              const reviewedDate = formatDateWithWeekday(selectedInfraction.reviewed_at);
                              return reviewedDate.isWeekend ? "bold" : "normal";
                            })()}
                          >
                            {(() => {
                              const reviewedDate = formatDateWithWeekday(selectedInfraction.reviewed_at);
                              return reviewedDate.text;
                            })()}
                          </Text>
                        </Box>
                      </>
                    )}

                    {selectedInfraction.plate && (
                      <>
                        <Divider />
                        <Box>
                          <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold" mb={1}>
                            Placa
                          </Text>
                          <PlateWithSuggestion
                            plate={selectedInfraction.plate}
                            registeredVehicleType={selectedInfraction.registeredVehicleType}
                            suggestion={getActivePlateSuggestion(selectedInfraction)}
                            isAccepted={Boolean(acceptedPlateCorrections[selectedInfraction.id])}
                            onAccept={() => {
                              const suggestion = getActivePlateSuggestion(selectedInfraction);
                              if (suggestion) {
                                handleAcceptPlateSuggestion(selectedInfraction.id, suggestion);
                              }
                            }}
                            onReject={() => handleRejectPlateSuggestion(selectedInfraction.id)}
                            layout="inline"
                          />
                        </Box>
                      </>
                    )}
                  </VStack>
                </VStack>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box >
  );
};

export default Batches;